import * as Sentry from '@sentry/react';

import { createElement } from 'react';

import { backoffWithJitter } from '@anycable/core';
import { Controller } from '@hotwired/stimulus';
import { liveQuery } from 'dexie';
import { createRoot } from 'react-dom/client';
import { decodeTime, ulid } from 'ulid';

import { isLocallyRetainedWidgetChat } from '../lib/agent_chats';
import { apiDelete, apiPost, apiRecoveryRead } from '../lib/api_v2';
import { mergeCachedRecord } from '../lib/cacheMerge';
import { ensureCatchupEnvelope, rotateCatchupEnvelope } from '../lib/catchupEnvelopes';
import db, { clearFingerprint, hasDataChanged, storeFingerprint } from '../lib/db';
import { assignDocument } from '../lib/desktop_navigation_diagnostics';
import {
  directMessageWorkspaceIdSet,
  replaceVisibleDirectMessages,
  storeDirectMessagePayload,
} from '../lib/direct_messages_store';
import { log, logDebug, logError, logFatal, logWarn } from '../lib/logger';
import {
  MediaAttachmentOwnerTypes,
  MediaAttachmentStates,
  getSnapshot as getMediaAttachmentSnapshot,
  removeMediaAttachmentsByUploadIds,
  retryMediaAttachmentsByUploadIds,
  setMediaAttachmentsOwner,
  subscribe as subscribeMediaAttachments,
} from '../lib/media_attachments_store';
import { allOptimisticMessages, messageById, messagesByChannelAndSortOrder, messagesByPseudotypeAndSortOrder } from '../lib/messageStore';
import { currentProfileId, currentUserId, currentWorkspaceId, rootDomainUrl } from '../lib/meta';
import { bootOneSignal } from '../lib/onesignal';
import {
  COMPLETED_STATUS,
  FAILED_ATTACHMENT_STATUS,
  FAILED_STATUS,
  OPTIMISTIC_MESSAGE_MAX_RETRIES,
  SENDING_STATUS,
  WAITING_FOR_ATTACHMENTS_STATUS,
  createOptimisticMessage,
  getUploadIds,
  optimisticMessageOwnerId,
  resolveOptimisticSortOrder,
} from '../lib/optimistic_messages';
import { bulkPutMergedChannelReadMarkers, putMergedChannelReadMarker } from '../lib/read_cursor';
import { initSentry } from '../lib/sentry';
import { hasVisibleWorkspace } from '../lib/visibleWorkspace';
import { mergeAgentChatWithGenerationTurn, storeAgentChatWithGenerationTurn } from '../lib/widgetGenerationTurns';
import { markWorkspaceRailBootstrapReady, resetWorkspaceRailBootstrapReady } from '../lib/workspaceRailBootstrap';
import {
  deleteWorkspaceLatestMessageBySortOrder,
  purgeWorkspaceLatestMessages,
  replaceWorkspaceLatestMessages,
  syncWorkspaceLatestMessageChanges,
  WORKSPACE_LATEST_MESSAGES_REFRESH_EVENT,
} from '../lib/workspaceLatestMessages';
import { pruneStaleWorkspaces } from '../lib/workspaceSnapshot';
import {
  authorizeWorkspaceWidgetBoardsFromMembershipSnapshot,
  revokeWorkspaceWidgetBoard,
} from '../lib/workspaceWidgetBoardStore';

import cable from '../cable';
import SharedChannelApp from '../components/SharedChannelApp';
import SurfaceErrorBoundary from '../components/shared/SurfaceErrorBoundary';
import { LIVE_MESSAGE_EVENT, LIVE_REACTION_EVENT } from '../components/chat/events';
import {
  purgeFeedWorkspace,
  resetAllFeedReplicatorChannels,
  resetFeedReplicatorChannelFromCatchup,
  startFeedReplicator,
  stopFeedReplicator,
} from '../feed/echo/feedReplicatorBootstrap';
import { profileRowsNeedRefresh } from '../feed/echo/profileStore';
import MainLayout from '../components/layouts/MainLayout';
import { DocumentTitleProvider } from '../hooks/useDocumentTitleEvents';

initSentry();
bootOneSignal();

const SCROLL_STATE_STORAGE_PREFIX = 'output:scroll';
const OPTIMISTIC_MESSAGE_INITIAL_RETRY_DELAY_MS = 1000;
const OPTIMISTIC_MESSAGE_MAX_RETRY_DELAY_MS = 30000;

const LOADER_MAX_ATTEMPTS = 3;
const LOADER_RETRY_DELAY_MS = 600;
const MESSAGE_BOOTSTRAP_MAX_CHANNELS = 50;
const RAIL_MESSAGE_BOOTSTRAP_FRESHNESS_MS = 5000;
const SHARED_CHANNEL_ENTITY_SNAPSHOT_COLLECTIONS = new Set(['profiles', 'channels', 'emojis']);



function clearAllScrollStates() {
  try {
    const prefix = `${SCROLL_STATE_STORAGE_PREFIX}:`;
    const keys = [];

    for (let index = 0; index < window.localStorage.length; index++) {
      const key = window.localStorage.key(index);
      if (typeof key === 'string' && key.startsWith(prefix)) keys.push(key);
    }

    keys.forEach((key) => {
      window.localStorage.removeItem(key);
    });
  } catch {
    // no-op
  }
}

// Manifest refresh + resubscribe after a stream closes unexpectedly; capped so a permanently rejected key retries about once a minute.
const subscriptionReconcileDelay = backoffWithJitter(2000, { maxInterval: 60_000 });

// Retry API-backed store refreshes, then force a full reload rather than leave Dexie stale.
async function runLoaderWithRetries(label, runOnce) {
  let lastError;
  for (let attempt = 1; attempt <= LOADER_MAX_ATTEMPTS; attempt++) {
    try {
      await runOnce();
      return;
    } catch (error) {
      lastError = error;
      logError('live-journal', `[reload-debug] ${label} failed (attempt ${attempt}/${LOADER_MAX_ATTEMPTS}):`, error);
      if (attempt < LOADER_MAX_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, LOADER_RETRY_DELAY_MS));
      }
    }
  }
  const errorMessage = lastError instanceof Error ? lastError.message : String(lastError);
  logError('live-journal', `${label} exhausted retries; dispatching output:reload`, lastError);
  window.dispatchEvent(
    new CustomEvent('output:reload', {
      detail: {
        source: 'live-journal',
        reason: 'loader-retries-exhausted',
        label,
        error: errorMessage,
      },
    }),
  );
  throw lastError;
}

export default class extends Controller {
  static values = {
    initialChannelId: String,
    shareKey: { type: String, default: null },
  };

  get isSharedChannel() {
    return this.shareKeyValue !== null;
  }

  manifest = [];

  collectionHooks = {};
  workspaceKillInProgressIds = new Set();
  messageBootstrapRequests = new Map();
  railMessageBootstrapCompletedAt = 0;

  // subscription management by composite keys
  rootSubscriptions = new Map();
  profileSubscriptions = new Map();
  channelSubscriptions = new Map();
  subscriptionReconcileTimer = null;
  subscriptionReconcileAttempts = 0;
  subscriptionEpoch = 0;
  subscriptionsActive = false;
  lostSubscriptions = new Map();

  processedOptimisticSortOrdersFromRealMessages = new Set();

  // track cancelled reaction submissions to prevent them from being added when server responds
  cancelledReactionSubmissions = new Set();

  // track abort controllers for in-flight reaction submissions
  reactionAbortControllers = new Map();

  // track pending reaction submissions to avoid duplicates
  pendingReactionSubmissions = new Set();

  // track pending message submissions to avoid duplicate optimistic retries
  pendingSubmissions = new Set();

  // re-check attachment-backed optimistic messages when upload state changes
  attachmentBackedMessageCheckPromise = null;
  attachmentBackedMessageCheckRequested = false;
  mediaAttachmentSubscriptionCleanup = null;

  // Track pending shared channel reloads to avoid duplicate requests
  pendingSharedChannelReload = false;

  connected = null;

  composite(...parts) {
    return parts.map((p) => String(p)).join('\0');
  }

  sharedChannelFingerprintScope() {
    const workspaceId = currentWorkspaceId();
    const channelId = this.initialChannelIdValue;
    return workspaceId && channelId ? `${workspaceId}:${channelId}` : null;
  }

  collectionFingerprintKey(collection) {
    if (!this.isSharedChannel || !SHARED_CHANNEL_ENTITY_SNAPSHOT_COLLECTIONS.has(collection)) return collection;
    const scope = this.sharedChannelFingerprintScope();
    return scope ? `${collection}:${scope}` : collection;
  }

  async clearCollectionFingerprint(collection) {
    await clearFingerprint(collection);
    const scopedKey = this.collectionFingerprintKey(collection);
    if (scopedKey !== collection) await clearFingerprint(scopedKey);
  }

  decompose(compositeKey) {
    return compositeKey.split('\0');
  }

  async isDirectMessageWorkspaceId(workspaceId) {
    if (!workspaceId) return false;
    return (await this.directMessageWorkspaceIds()).has(String(workspaceId));
  }

  // Cache the DM workspace-id Set per controller instance. Catchup envelopes ask
  // about it once per journal entry (across multiple processors) and every bulk
  // loader queries it to skip pruning DM-owned rows. Recomputing on each call
  // means a full `db.direct_messages.toArray()` per touch.
  async directMessageWorkspaceIds() {
    if (!this._directMessageWorkspaceIdsPromise) {
      const directMessageWorkspaceIdsPromise = directMessageWorkspaceIdSet().catch((error) => {
        // Only clear the cache for the failed read that is still active; a later
        // invalidation may already have replaced it with a fresh promise.
        if (this._directMessageWorkspaceIdsPromise === directMessageWorkspaceIdsPromise) {
          this.invalidateDirectMessageWorkspaceIdCache();
        }
        throw error;
      });
      this._directMessageWorkspaceIdsPromise = directMessageWorkspaceIdsPromise;
    }
    return this._directMessageWorkspaceIdsPromise;
  }

  invalidateDirectMessageWorkspaceIdCache() {
    this._directMessageWorkspaceIdsPromise = null;
  }

  initialize() {
    logDebug('live-journal', 'Initializing');

    // publish the controller for external use and use within callbacks
    window.livejournal = this;

    // publish the cable instance for debug/inspection purposes. this is subject to change and nothing here should be considered a stable API.
    window.__debug__cable = cable;
  }

  dispatchLiveMessageEvent(action, data, cursor = null) {
    if (!action || !data) return;
    window.dispatchEvent(new CustomEvent(LIVE_MESSAGE_EVENT, { detail: { action, cursor, data } }));
  }

  normalizeOptimisticRetryMetadata(message = {}) {
    const retryMaxAttemptsRaw = Number(message.retry_max_attempts);
    const retryMaxAttempts =
      Number.isFinite(retryMaxAttemptsRaw) && retryMaxAttemptsRaw > 0
        ? Math.max(1, Math.floor(retryMaxAttemptsRaw))
        : OPTIMISTIC_MESSAGE_MAX_RETRIES;

    const retryAttemptRaw = Number(message.retry_attempt);
    const retryAttempt =
      Number.isFinite(retryAttemptRaw) && retryAttemptRaw >= 0 ? Math.min(Math.floor(retryAttemptRaw), retryMaxAttempts) : 0;

    return { retryAttempt, retryMaxAttempts };
  }

  computeOptimisticRetryDelay({ retryAttempt = 1, initialDelayMs = OPTIMISTIC_MESSAGE_INITIAL_RETRY_DELAY_MS } = {}) {
    const attempt = Math.max(1, Math.floor(retryAttempt));
    const jitterMs = Math.floor(Math.random() * 1000);
    const exponentialMs = initialDelayMs * 2 ** (attempt - 1);
    return Math.min(exponentialMs + jitterMs, OPTIMISTIC_MESSAGE_MAX_RETRY_DELAY_MS);
  }

  async connect() {
    logDebug('live-journal', 'Connecting');
    resetWorkspaceRailBootstrapReady();

    document.body.classList.add('overflow-hidden', 'overscroll-x-none');

    // setup event handlers first so we don't miss any events before they're attached
    this.setupEventHandlers();
    this.setupAttachmentBackedMessageWatcher();

    // lost-stream reconciliation may schedule work from here until cleanupSubscriptions
    this.subscriptionsActive = true;

    // initialize catchup queue
    this.catchups = new Set();
    this.catchup_dirty_kinds = null;
    this.catchup_dirty_message_channel_ids = null;

    // setup dexie collection listeners
    this.setupCollectionListeners();

    // start tracking events
    this.setupTracking();

    // mount the root app component
    this.mount();

    // replicate live message events into the feed tables for the page lifetime; shadow
    // shared-channel sessions have no owned profiles to replicate under
    if (!this.isSharedChannel) startFeedReplicator();

    // perform initial load now that we are connected and app is mounted
    await Promise.all([this.loadManifest(), this.loadRootData()]);
    await this.subscribeToManifestChannels();

    // begin processing catchup envelopes
    this.processCatchupEnvelopes();

    // resume retries for optimistic messages that were interrupted by page reload
    this.resumeOptimisticMessageRetries();

    // purge old catchup_envelopes
    this.purgeOldCatchupEnvelopes();

    // fire the output:ready event to inform the native app everything is ready
    window.dispatchEvent(new CustomEvent('output:ready'));

    // mark a prop on window that we sent the ready event
    window.output = window.output || {};
    window.output.ready = Date.now();
  }

  disconnect() {
    logDebug('live-journal', 'Disconnecting');

    document.body.classList.remove('overflow-hidden', 'overscroll-x-none');

    // unmount the root app
    if (this.app) this.app.unmount();

    stopFeedReplicator();

    // reset state
    this.connected = null;

    // cleanup
    this.cleanupSubscriptions();
    this.cleanupEventHandlers();
    this.cleanupAttachmentBackedMessageWatcher();
    this.cleanupTracking();
  }

  async mount() {
    this.app = createRoot(this.element, {
      // Callback called when an error is thrown and not caught by an ErrorBoundary.
      onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
        logWarn('live-journal', 'Uncaught error', error, errorInfo.componentStack);
      }),

      // Callback called when React catches an error in an ErrorBoundary.
      onCaughtError: Sentry.reactErrorHandler(),

      // Callback called when React automatically recovers from errors.
      onRecoverableError: Sentry.reactErrorHandler(),
    });

    // The last line of defense: without a boundary here, any render error that slips
    // past the surface-level boundaries unmounts the root and leaves a white page.
    if (this.isSharedChannel) {
      this.app.render(
        createElement(
          SurfaceErrorBoundary,
          { surface: 'shared-channel-root', className: 'h-dvh' },
          createElement(
            DocumentTitleProvider,
            null,
            createElement(SharedChannelApp, {
              channelId: this.initialChannelIdValue,
            }),
          ),
        ),
      );
    } else {
      this.app.render(
        createElement(
          SurfaceErrorBoundary,
          { surface: 'app-root', className: 'h-dvh' },
          createElement(DocumentTitleProvider, null, createElement(MainLayout)),
        ),
      );
    }
  }

  async loadManifest() {
    const { response, data } = await apiRecoveryRead('/journals/manifest');

    if (!(response.ok && data.ok)) {
      throw new Error(`Failed to fetch journal manifest: ${(Array.isArray(data.errors) ? data.errors : []).join(', ') || 'Unknown error'}`);
    }

    this.manifest = data.data.collections || [];
    logDebug('live-journal', `Loaded journal manifest with ${this.manifest.length} collections`);

    // derive the set of profile ids we're subscribed to (one per workspace) so the
    // subscription router can distinguish profile-scoped collections from channel ones
    this.profileIds = new Set(this.manifest.filter((c) => c.scope === 'profile').map((c) => c.collection_name));
  }

  async killYourSelf() {
    // dim the screen
    document.body.style.opacity = '0.1';
    document.body.style.pointerEvents = 'none';

    // clear persisted scroll restoration points
    clearAllScrollStates();

    // stop feed replication before the database goes away underneath it
    stopFeedReplicator();

    // delete the database
    await db.delete();

    // reload the app
    window.dispatchEvent(new CustomEvent('output:reload', { detail: { source: 'live-journal', reason: 'database-reset' } }));
  }

  async getNextWorkspaceAfterKill(workspaceId) {
    const workspaces = await db.workspaces.orderBy('slug').toArray();
    const currentIndex = workspaces.findIndex((workspace) => String(workspace.id) === String(workspaceId));
    const candidates = workspaces.filter((workspace) => {
      if (String(workspace.id) === String(workspaceId)) return false;
      return !workspace.slug?.startsWith('__deactivated__');
    });

    if (candidates.length === 0) return null;
    if (currentIndex < 0) return candidates[0];

    for (let offset = 1; offset < workspaces.length; offset += 1) {
      const workspace = workspaces[(currentIndex + offset) % workspaces.length];
      if (!workspace || String(workspace.id) === String(workspaceId)) continue;
      if (workspace.slug?.startsWith('__deactivated__')) continue;
      return workspace;
    }

    return candidates[0];
  }

  getWorkspaceHomeUrl(workspace) {
    const workspaceId = String(workspace.id);
    const mainChannelId = workspace.main_channel_id ? String(workspace.main_channel_id) : null;
    const path = mainChannelId ? `/spaces/${workspaceId}/chats/${mainChannelId}` : `/spaces/${workspaceId}/chats`;
    return rootDomainUrl(path);
  }

  async killWorkspace(payload) {
    // accept the workspaceId directly or via a CustomEvent detail object
    const workspaceId = typeof payload === 'string' ? payload : payload?.detail?.workspaceId;
    if (!workspaceId) return;

    const workspaceIdString = String(workspaceId);
    if (this.workspaceKillInProgressIds.has(workspaceIdString)) return;

    this.workspaceKillInProgressIds.add(workspaceIdString);
    try {
      // Fence first: a snapshot response already in flight must not be able to
      // recreate board rows after membership loss.
      await revokeWorkspaceWidgetBoard(workspaceIdString);
      const isCurrent = workspaceIdString === String(currentWorkspaceId());
      const nextWorkspace = isCurrent ? await this.getNextWorkspaceAfterKill(workspaceIdString) : null;
      if (isCurrent) {
        document.body.style.opacity = '0.1';
        document.body.style.pointerEvents = 'none';
      }

      logDebug('live-journal', 'Purging workspace', workspaceIdString);
      try {
        await purgeFeedWorkspace(workspaceIdString);
      } catch (error) {
        if (isCurrent) {
          logError('live-journal', 'Feed workspace purge failed; resetting local database', error);
          await this.killYourSelf();
          return;
        }
        // A full reset would discard every workspace's local state (pending uploads
        // included) over one isolated cleanup failure, so background kills log the
        // feed remnant and continue their scoped deletes instead.
        logError('live-journal', 'Feed workspace purge failed; continuing background workspace kill', error);
      }
      await db.workspaces.delete(workspaceIdString);

      logDebug('live-journal', 'Purging owned resources', workspaceIdString);
      await db._pending_upload_blobs.where('workspace_id').equals(workspaceIdString).delete();
      await db.agent_chats.where('workspace_id').equals(workspaceIdString).delete();
      await db.activities.where('workspace_id').equals(workspaceIdString).delete();
      await db.board_items.where('workspace_id').equals(workspaceIdString).delete();
      await db.channels.where('workspace_id').equals(workspaceIdString).delete();
      await db.channel_read_markers.where('workspace_id').equals(workspaceIdString).delete();
      await db.emojis.where('workspace_id').equals(workspaceIdString).delete();
      await db.media_attachments.where('workspace_id').equals(workspaceIdString).delete();
      await db.mentions.where('workspace_id').equals(workspaceIdString).delete();
      await db.messages.where('workspace_id').equals(workspaceIdString).delete();
      await db.profiles.where('workspace_id').equals(workspaceIdString).delete();
      await db.widgets.where('workspace_id').equals(workspaceIdString).delete();
      await purgeWorkspaceLatestMessages(workspaceIdString);

      if (isCurrent) {
        if (nextWorkspace) {
          const nextWorkspaceUrl = this.getWorkspaceHomeUrl(nextWorkspace);
          logDebug('live-journal', 'Redirecting to next workspace', nextWorkspace.id);
          assignDocument(nextWorkspaceUrl, 'workspace-switch');
        } else {
          logDebug('live-journal', 'Redirecting to root url');
          assignDocument(rootDomainUrl('/'), 'workspace-switch');
        }
      }
    } finally {
      this.workspaceKillInProgressIds.delete(workspaceIdString);
    }
  }

  // === tracking ===

  setupTracking() {
    // setup catchup envelope tracking
    this.catchup_envelope_tracking_handle = cable.on('keepalive', this.bumpOpenCatchupEnvelope.bind(this));
    this.bumpOpenCatchupEnvelope();

    // setup visibility tracking
    this.visibility_tracking_handle = this.handleCatchupVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this.visibility_tracking_handle);
  }

  // === catchup logic ===

  async handleCatchupVisibilityChange() {
    // ignore visibility change if we are not connected
    if (!this.connected) return;

    resetAllFeedReplicatorChannels();

    if (document.visibilityState === 'visible') {
      if (!(await this.prepareVisibleCatchup())) return;
    }

    // create a new catchup envelope when the page is becoming visible or hidden
    await this.createNewCatchupEnvelope();
  }

  async prepareVisibleCatchup() {
    await rotateCatchupEnvelope(db);
    if (!this.isSharedChannel && !(await this.refreshWorkspaceMembershipsForCatchup())) return false;
    await this.refreshDirectMessagesAndManifest();
    return true;
  }

  async createNewCatchupEnvelope() {
    await ensureCatchupEnvelope(db, { bump: this.connected && document.visibilityState === 'visible' });
  }

  async bumpOpenCatchupEnvelope() {
    // only bump while the page is visible and we are connected
    if (!this.connected || document.visibilityState !== 'visible') return;

    // bump since timestamp of the open catchup envelope to now (update does not clobber other fields)
    await db._catchup_envelopes.where('status').equals('open').modify((envelope) => {
      envelope.since = Date.now();
    });
  }

  async purgeOldCatchupEnvelopes() {
    try {
      const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000; // 1 week in milliseconds

      // get all envelopes
      const allEnvelopes = await db._catchup_envelopes.toArray();

      // keep active envelopes (open and ready) and envelopes less than 1 week old
      const toDelete = allEnvelopes.filter((envelope) => {
        // always keep active envelopes
        if (envelope.status === 'open' || envelope.status === 'ready') return false;

        // skip envelopes without since timestamp (shouldn't happen, but be safe)
        if (!envelope.since) return false;

        // delete if older than 1 week
        return envelope.since < oneWeekAgo;
      });

      if (toDelete.length > 0) {
        const deleteIds = toDelete.map((e) => e.id);
        await db._catchup_envelopes.bulkDelete(deleteIds);
        logDebug('catchup', `Purged ${deleteIds.length} old catchup_envelopes (over 1 week old)`);
      }
    } catch (error) {
      logError('catchup', 'Failed to purge old catchup_envelopes', error);
    }
  }

  processCatchupEnvelopes() {
    const query = liveQuery(() => db._catchup_envelopes.where('status').equals('ready').toArray());
    this.process_catchup_envelopes_subscription = query.subscribe({
      next: (result) => {
        for (const envelope of result) {
          if (!this.catchups.has(envelope.id)) {
            logDebug('catchup', 'Adding catchup envelope to queue', envelope);
            this.catchups.add(envelope.id);
          }
        }
      },
      error: (error) => logError('catchup', 'Error in catchup envelope processing:', error),
    });

    this.process_catchup_envelopes_in_flight = null;
    this.process_catchup_envelopes_interval = setInterval(async () => {
      // bail if we are not connected
      if (!this.connected) return;

      // bail if an envelope is already in flight
      if (this.process_catchup_envelopes_in_flight) return;

      // grab the next envelope from the queue
      const envelopeId = this.catchups.values().next().value;
      if (!envelopeId) return;

      this.process_catchup_envelopes_in_flight = envelopeId;
      try {
        logDebug('catchup', 'Processing catchup envelope', envelopeId);
        await this.fetchCatchupEnvelopeJournalEntries(envelopeId);
      } finally {
        if (this.process_catchup_envelopes_in_flight === envelopeId) this.process_catchup_envelopes_in_flight = null;
      }
    }, 1000);
  }

  async fetchCatchupEnvelopeJournalEntries(id) {
    const envelope = await db._catchup_envelopes.get(id);
    if (!envelope) {
      logError('catchup', `Catchup envelope ${id} not found, skipping`);
      this.catchups.delete(id);
      return;
    }

    if (!envelope.since || !envelope.until) {
      logDebug('catchup', `Timestamps are invalid, cancelling catchup envelope`);
      await db._catchup_envelopes.where('id').equals(id).modify({ status: 'error', error: 'Timestamps are invalid' });
      this.catchups.delete(id);
      return;
    }

    const until = Math.floor(envelope.until * 0.001);
    let current_since = Math.floor(envelope.since * 0.001);
    if (current_since >= until) {
      logDebug('catchup', `Until ${until} is greater than or equal to since ${current_since}, cancelling catchup envelope`);
      await db._catchup_envelopes.where('id').equals(id).modify({ status: 'error', error: 'Until is greater than or equal to since' });
      this.catchups.delete(id);
      return;
    }

    const key_entries = this.manifest.filter((c) => c.key).map((c) => ({ workspace_id: c.workspace_id, user_id: c.user_id, key: c.key }));
    if (key_entries.length === 0) {
      logDebug('catchup', `Skipping catchup for envelope ${id} (no manifest keys)`);
      await db._catchup_envelopes.where('id').equals(id).modify({ status: 'done' });
      this.catchups.delete(id);
      return;
    }

    resetAllFeedReplicatorChannels();

    let has_more = true;
    let page_count = 0;
    let total_entries = 0;

    const recreation = {
      status: 'ready',
      since: envelope.since,
      until: envelope.until,
      original_id: envelope.original_id || id,
      attempts: (envelope.attempts || 0) + 1,
    };

    this.catchup_dirty_kinds = new Set();
    this.catchup_dirty_message_channel_ids = new Set();
    try {
      while (has_more) {
        try {
          const { response, data } = await apiRecoveryRead('/journals/fetch', { json: { since: current_since, until, key_entries } });

          // retry if the response is not ok
          if (!(response.ok && data.ok)) {
            const error = (Array.isArray(data.errors) ? data.errors : []).join(', ') || 'Unknown error';
            logError('catchup', `Error during catchup envelope ${id}, recreating catchup envelope`, error);
            await db._catchup_envelopes.where('id').equals(id).modify({ status: 'error', error });
            await db._catchup_envelopes.add({ ...recreation, retry_reason: 'api error' });
            this.catchups.delete(id);
            return;
          }

          const entries = data.data.journal_entries || [];
          has_more = data.data.has_more || false;

          logDebug('catchup', `Fetched envelope ${id} with ${entries.length} entries (page ${page_count + 1}), has_more: ${has_more}`);

          for (const entry of entries) {
            await this.processJournalEntry(entry);
            if (entry.sort_order) current_since = entry.sort_order;
          }

          total_entries += entries.length;
          page_count++;

          await db._catchup_envelopes.where('id').equals(id).modify({ current_since, total_entries, page_count, has_more });

          // abort and execute KYS event if we reach the safety limit
          if (page_count > 25) {
            logWarn('catchup', `Envelope ${id} reached page #${page_count} over safety limit, aborting fetch and executing KYS event`);
            await db._catchup_envelopes.where('id').equals(id).modify({ status: 'error', error: 'Page count exceeded safety limit' });
            // skip outer `finally` flush — db is about to be deleted and the page reloads
            this.catchup_dirty_kinds = null;
            this.catchup_dirty_message_channel_ids = null;
            await this.killYourSelf();
            // don't advance to the next envelope since we are purging everything anyway
            return;
          }
        } catch (error) {
          // retry if we get an error
          logError('catchup', `Error during catchup envelope ${id}, recreating catchup envelope`, error);
          await db._catchup_envelopes.where('id').equals(id).modify({ status: 'error', error: error.message });
          await db._catchup_envelopes.add({ ...recreation, retry_reason: 'api exception' });
          this.catchups.delete(id);
          return;
        }
      }
    } finally {
      await this.flushCatchupCoalescedFetches();
    }

    // mark the envelope as done so the next one can be processed
    log('catchup', `Envelope ${id} catchup complete: ${total_entries} entries in ${page_count} pages`);
    await db._catchup_envelopes.where('id').equals(id).modify({ status: 'done' });
    this.catchups.delete(id);
  }

  // bulk GETs for dirty kinds after catchup; clears `catchup_dirty_kinds` when done
  async flushCatchupCoalescedFetches() {
    const kinds = this.catchup_dirty_kinds;
    const dirtyMessageChannelIds = [...(this.catchup_dirty_message_channel_ids || [])];
    try {
      if (!kinds || kinds.size === 0) return;

      if (kinds.has('channels')) {
        await this.clearCollectionFingerprint('channels');
        await this.loadChannels(true);
      }

      // skip when `channels` ran — loadChannels(true) already loads read_markers
      if (kinds.has('channel_read_markers') && !kinds.has('channels')) {
        await this.loadChannelReadMarkers();
      }

      if (kinds.has('profiles')) {
        await this.clearCollectionFingerprint('profiles');
        await this.loadProfiles();
      }

      if (kinds.has('emojis')) {
        await this.clearCollectionFingerprint('emojis');
        await this.loadEmojis();
      }

      if (kinds.has('widgets')) {
        await clearFingerprint('widgets');
        await this.loadWidgets();
      }

      if (kinds.has('board_items')) {
        await clearFingerprint('board_items');
        await this.loadBoardItems();
      }

      if (!this.isSharedChannel && kinds.has('agent_chats')) {
        await clearFingerprint('agent_chats');
        await this.loadAgentChats();
      }

      if (!this.isSharedChannel && kinds.has('mentions')) {
        await clearFingerprint('mentions');
        await this.loadMentions();
      }

      if (!this.isSharedChannel && kinds.has('activities')) {
        await clearFingerprint('activities');
        await this.loadActivities();
      }

      if (kinds.has('workspaces')) {
        await clearFingerprint('workspaces');
        await this.loadWorkspaces();
      }

      if (kinds.has('users')) {
        await this.loadMe();
      }

      if (kinds.has('direct_messages')) {
        await this.refreshDirectMessagesAndManifest();
      }

      if (kinds.has('workspace_latest_messages')) {
        if (dirtyMessageChannelIds.length > 0) await this.loadRailMessageBootstrapForChannels(dirtyMessageChannelIds);
        else await this.loadMessageBootstrap({ force: true });
      }
    } finally {
      this.catchup_dirty_kinds = null;
      this.catchup_dirty_message_channel_ids = null;
    }
  }

  async processJournalEntry(entry) {
    if (!this.catchup_dirty_kinds) {
      logError('catchup', 'processJournalEntry called without catchup_dirty_kinds (caller must assign a Set before the journal loop)');
      return;
    }

    const { action, reference_kind, collection_name } = entry;
    logDebug('live-journal', `[${reference_kind}][${action}] Processing item in ${collection_name} collection`);

    // handle different resource types
    switch (reference_kind) {
      case 'board_item':
        await this.processBoardItemJournalEntry(entry);
        break;
      case 'emoji':
        await this.processEmojiJournalEntry(entry);
        break;
      case 'widget':
        await this.processWidgetJournalEntry(entry);
        break;
      case 'channel':
        await this.processChannelJournalEntry(entry);
        break;
      case 'profile':
        await this.processProfileJournalEntry(entry);
        break;
      case 'agent_chat':
        await this.processAgentChatJournalEntry(entry);
        break;
      case 'mention':
        await this.processMentionJournalEntry(entry);
        break;
      case 'activity':
        await this.processActivityJournalEntry(entry);
        break;
      case 'message':
        await this.processMessageJournalEntry(entry);
        break;
      case 'channel_read_marker':
        await this.processChannelReadMarkerJournalEntry(entry);
        break;
      case 'workspace':
        await this.processWorkspaceJournalEntry(entry);
        break;
      case 'user':
        await this.processUserJournalEntry(entry);
        break;
      case 'direct_message':
        this.catchup_dirty_kinds.add('direct_messages');
        break;
      default:
        logWarn('live-journal', `Unhandled journal entry type: ${reference_kind}`);
    }
  }

  async loadWidget(widgetId) {
    await runLoaderWithRetries(`loadWidget(${widgetId})`, async () => {
      const { response, data } = await apiRecoveryRead(`/widgets/${widgetId}`);
      if (!(response.ok && data.ok && data.data?.widget)) {
        throw new Error(
          `Failed to fetch widget ${widgetId}: ${(Array.isArray(data.errors) ? data.errors : []).join(', ') || 'Unknown error'}`,
        );
      }

      await db.widgets.put(data.data.widget);
      await clearFingerprint('widgets');
    });
  }

  async fetchAndStoreBoardItem(boardItemId) {
    const { response, data } = await apiRecoveryRead(`/board_items/${boardItemId}`);
    if (response.ok && data.ok && data.data?.board_item) {
      await db.board_items.put(data.data.board_item);
      await clearFingerprint('board_items');
    }
  }

  async fetchAndStoreAgentChat(agentChatId) {
    const { response, data } = await apiRecoveryRead(`/agent_chats/${agentChatId}`);
    if (response.ok && data.ok && data.data?.agent_chat) {
      await storeAgentChatWithGenerationTurn(data.data.agent_chat);
      await clearFingerprint('agent_chats');
    }
  }

  // === journal entry processors ===

  async processBoardItemJournalEntry(entry) {
    const { action, reference_item, workspace_id } = entry;

    if (action === 'destroy') {
      await db.board_items
        .where('[workspace_id+id]')
        .equals([String(workspace_id), String(reference_item)])
        .delete();
      await clearFingerprint('board_items');
    } else if (await this.isDirectMessageWorkspaceId(workspace_id)) {
      await this.fetchAndStoreBoardItem(reference_item);
    } else {
      this.catchup_dirty_kinds.add('board_items');
    }
  }

  async processEmojiJournalEntry(entry) {
    const { action, reference_item, workspace_id } = entry;

    if (action === 'destroy') {
      await db.emojis
        .where('[workspace_id+id]')
        .equals([String(workspace_id), String(reference_item)])
        .delete();
      await this.clearCollectionFingerprint('emojis');
    } else {
      this.catchup_dirty_kinds.add('emojis');
    }
  }

  async processWidgetJournalEntry(entry) {
    const { action, reference_item, workspace_id } = entry;

    if (action === 'destroy') {
      await db.widgets
        .where('[workspace_id+id]')
        .equals([String(workspace_id), String(reference_item)])
        .delete();
      await clearFingerprint('widgets');
    } else if (await this.isDirectMessageWorkspaceId(workspace_id)) {
      await this.loadWidget(reference_item);
    } else {
      this.catchup_dirty_kinds.add('widgets');
    }
  }

  async processChannelReadMarkerJournalEntry(entry) {
    const { action, reference_item, workspace_id } = entry;

    if (action === 'destroy') {
      await db.channel_read_markers.delete([String(workspace_id), String(reference_item)]);
    } else if (await this.isDirectMessageWorkspaceId(workspace_id)) {
      this.catchup_dirty_kinds.add('direct_messages');
    } else {
      this.catchup_dirty_kinds.add('channel_read_markers');
    }
  }

  async processChannelJournalEntry(entry) {
    const { action, reference_item, workspace_id } = entry;

    switch (action) {
      case 'destroy':
        await this.clearCollectionFingerprint('channels');
        // remove channel and trigger cleanup
        await db.channels.bulkDelete([
          [String(workspace_id), `skeleton_${reference_item}`],
          [String(workspace_id), String(reference_item)],
        ]);
        this.unsubscribeFromChannel(reference_item);
        break;

      case 'create':
      case 'update': {
        await db.channels.put({ id: `skeleton_${reference_item}`, workspace_id: String(workspace_id), pseudotype: 'skeleton' });
        this.catchup_dirty_kinds.add('channels');
        break;
      }
    }
  }

  async processMessageJournalEntry(entry) {
    const { action, reference_item, collection_name, workspace_id } = entry;
    const channelId = collection_name ? String(collection_name) : null;
    const workspaceId = String(workspace_id);

    // first check if there are any objects before calling primaryKeys (https://github.com/dexie/Dexie.js/issues/1030)
    const existing = await messagesByChannelAndSortOrder(channelId, reference_item).count();

    switch (action) {
      case 'destroy':
        if (existing !== 0) {
          const keys = await messagesByChannelAndSortOrder(channelId, reference_item).primaryKeys();
          if (keys.length) await db.messages.bulkDelete(keys);
        }
        await deleteWorkspaceLatestMessageBySortOrder(workspaceId, channelId, reference_item);
        break;
      case 'create':
      case 'update':
        if (existing === 0) await this.messagePut(this.skeletonDataForSortOrder(collection_name, reference_item, workspaceId), false);
        this.catchup_dirty_kinds.add('workspace_latest_messages');
        if (channelId) this.catchup_dirty_message_channel_ids.add(channelId);
        break;
    }

    // Catchups carry journal metadata rather than hydrated messages and their client-clock
    // envelope bounds cannot safely advance feed coverage; force the feed sync-first gate.
    if (channelId) resetFeedReplicatorChannelFromCatchup(workspaceId, channelId);
  }

  async processMentionJournalEntry(entry) {
    const { action, reference_item, workspace_id } = entry;
    const workspaceId = String(workspace_id);
    const sortOrder = String(reference_item);

    if (action === 'destroy') {
      const existing = await db.mentions.where('[workspace_id+sort_order]').equals([workspaceId, sortOrder]).count();
      if (existing !== 0) {
        const keys = await db.mentions.where('[workspace_id+sort_order]').equals([workspaceId, sortOrder]).primaryKeys();
        if (keys.length) await db.mentions.bulkDelete(keys);
      }
      await clearFingerprint('mentions');
    } else {
      this.catchup_dirty_kinds.add('mentions');
    }
  }

  async processActivityJournalEntry(entry) {
    const { action, reference_item, workspace_id } = entry;
    const workspaceId = String(workspace_id);
    const sortOrder = String(reference_item);

    if (action === 'destroy') {
      const existing = await db.activities.where('[workspace_id+sort_order]').equals([workspaceId, sortOrder]).count();
      if (existing !== 0) {
        const keys = await db.activities.where('[workspace_id+sort_order]').equals([workspaceId, sortOrder]).primaryKeys();
        if (keys.length) await db.activities.bulkDelete(keys);
      }
      await clearFingerprint('activities');
    } else {
      this.catchup_dirty_kinds.add('activities');
    }
  }

  async processProfileJournalEntry(entry) {
    const { action, reference_item, workspace_id } = entry;
    const profileId = String(reference_item);
    const workspaceId = String(workspace_id);

    if (action === 'destroy') {
      await db.profiles.delete([workspaceId, profileId]);
      await this.clearCollectionFingerprint('profiles');
    } else if (await this.isDirectMessageWorkspaceId(workspace_id)) {
      this.catchup_dirty_kinds.add('direct_messages');
    } else {
      this.catchup_dirty_kinds.add('profiles');
    }
  }

  async processWorkspaceJournalEntry(entry) {
    const { action, reference_item } = entry;
    const id = String(reference_item);

    if (action === 'destroy') {
      await this.killWorkspace(id);
      await clearFingerprint('workspaces');
    } else {
      this.catchup_dirty_kinds.add('workspaces');
    }
  }

  async processUserJournalEntry(entry) {
    const { action } = entry;

    if (action === 'destroy') {
      await db.users.clear();
    } else {
      this.catchup_dirty_kinds.add('users');
    }
  }

  async processAgentChatJournalEntry(entry) {
    const { action, reference_item, workspace_id } = entry;

    if (action === 'destroy') {
      await db.agent_chats
        .where('[workspace_id+id]')
        .equals([String(workspace_id), String(reference_item)])
        .delete();
      await clearFingerprint('agent_chats');
    } else if (await this.isDirectMessageWorkspaceId(workspace_id)) {
      await this.fetchAndStoreAgentChat(reference_item);
    } else {
      this.catchup_dirty_kinds.add('agent_chats');
    }
  }

  // === subscription management ===

  async subscribeToManifestChannels() {
    const manifest = this.manifest;

    // subscribe to root collections
    const rootEntries = manifest.filter((entry) => entry.collection_name === 'root');
    for (const entry of rootEntries) {
      this.subscribeToCollection(entry);
    }

    // subscribe to profile-specific collections (one profile per workspace from the manifest)
    const profileEntries = manifest.filter((entry) => this.profileIds?.has(entry.collection_name));
    for (const entry of profileEntries) {
      this.subscribeToCollection(entry);
    }

    // subscribe to channel-specific collections (across all workspaces from the v2 manifest)
    const channelEntries = manifest.filter((entry) => entry.scope === 'channel');
    for (const entry of channelEntries) {
      this.subscribeToCollection(entry);
    }

    // subscribe to user-specific collections carried by workspace providers
    const userEntries = manifest.filter((entry) => entry.scope === 'user');
    for (const entry of userEntries) {
      this.subscribeToCollection(entry);
    }
  }

  // Dedupe map and key for a manifest entry. `trackKey` is unique across the three maps and is
  // what lost-stream bookkeeping is keyed by.
  subscriptionSlotFor(manifestEntry) {
    const { reference_kind, collection_name, workspace_id, user_id } = manifestEntry;
    const root_id = user_id || workspace_id;
    const root_type = user_id ? 'user' : 'workspace';

    let name, map, key;
    if (collection_name === 'root') {
      [name, map, key] = ['root', this.rootSubscriptions, this.composite(root_type, root_id, reference_kind)];
    } else if (this.profileIds?.has(collection_name)) {
      [name, map, key] = ['profile', this.profileSubscriptions, this.composite(root_type, root_id, reference_kind)];
    } else {
      [name, map, key] = ['channel', this.channelSubscriptions, this.composite(root_type, root_id, collection_name, reference_kind)];
    }
    return { map, key, trackKey: `${name}:${key.replaceAll('\0', ':')}` };
  }

  subscribeToCollection(manifestEntry) {
    const { reference_kind, collection_name, key, workspace_id, user_id } = manifestEntry;
    const slot = this.subscriptionSlotFor(manifestEntry);
    if (slot.map.has(slot.key)) return;

    // create subscription for the collection with the given key
    const subscription = cable.subscribeTo('SignalJournalChannel', {
      workspace_id,
      user_id,
      signed_signal_journal_key: key,
    });
    subscription._trackKey = slot.trackKey;

    // Profile-scoped collections (mention, channel_read_marker, agent_chat) are the
    // delivery path for cross-workspace unread signals.

    // store unbind handles on subscription instance
    subscription._unbindOnMessage = subscription.on('message', async (message) => {
      await this.handleLiveJournalEvent(message, reference_kind);
    });
    // AnyCable closes a channel for good when its subscribe is rejected or goes
    // unacknowledged for 10s, and drops it from the hub so later cable reconnects
    // never retry it. Any close we did not ask for is a lost stream.
    subscription._unbindOnClose = subscription.on('close', (error) => this.handleSubscriptionClosed(subscription, error));
    subscription._unbindOnConnect = subscription.on('connect', () => this.handleSubscriptionRepaired(slot.trackKey));

    slot.map.set(slot.key, subscription);
    logDebug('live-journal', `[${reference_kind}] Subscribed to ${collection_name} collection`);
  }

  async subscribeToChannelCollections(channelId, workspaceId = null) {
    const channelEntries = this.manifest.filter(
      (entry) => entry.collection_name === channelId && (workspaceId == null || String(entry.workspace_id) === String(workspaceId)),
    );

    for (const entry of channelEntries) {
      this.subscribeToCollection(entry);
    }
  }

  releaseSubscription(subscription) {
    subscription._unbindOnMessage?.();
    subscription._unbindOnClose?.();
    subscription._unbindOnConnect?.();
    subscription.disconnect();
  }

  // Drops a subscription from whichever dedupe map holds it; false when it was already released.
  forgetSubscription(subscription) {
    for (const map of [this.rootSubscriptions, this.profileSubscriptions, this.channelSubscriptions]) {
      for (const [key, candidate] of map) {
        if (candidate !== subscription) continue;
        map.delete(key);
        return true;
      }
    }
    return false;
  }

  // === lost-stream reconciliation ===
  //
  // A stream is "lost" from the moment AnyCable closes it until a subscription for the same key
  // reports `connect` again. `lostSubscriptions` holds that set; the reconcile backoff grows while
  // it is non-empty and resets only once it drains, so an unrelated healthy stream reconnecting
  // cannot restart a failing key's backoff. `subscriptionEpoch` fences in-flight work across
  // controller teardown: every await re-checks it before touching the maps.

  handleSubscriptionClosed(subscription, error) {
    if (!this.forgetSubscription(subscription)) return;
    subscription._unbindOnMessage?.();
    subscription._unbindOnClose?.();
    subscription._unbindOnConnect?.();
    const key = subscription._trackKey;
    const reason = error?.reason ?? error?.name ?? 'unspecified';
    logWarn('live-journal', `Subscription closed unexpectedly, will reconcile: ${key}`, { error });
    Sentry.addBreadcrumb({ category: 'live-journal', level: 'warning', message: 'subscription closed unexpectedly', data: { key, reason } });
    // A key that keeps failing stays reported until it is actually repaired; no Sentry event per retry.
    const reported = this.lostSubscriptions.get(key)?.reported ?? false;
    this.lostSubscriptions.set(key, { reason, reported });
    this.scheduleSubscriptionReconcile();
  }

  handleSubscriptionRepaired(key) {
    if (!this.lostSubscriptions.delete(key)) return;
    if (this.lostSubscriptions.size === 0) this.subscriptionReconcileAttempts = 0;
  }

  scheduleSubscriptionReconcile() {
    if (!this.subscriptionsActive || this.subscriptionReconcileTimer) return;
    const epoch = this.subscriptionEpoch;
    const delay = subscriptionReconcileDelay(this.subscriptionReconcileAttempts);
    this.subscriptionReconcileAttempts += 1;
    this.subscriptionReconcileTimer = setTimeout(() => {
      this.subscriptionReconcileTimer = null;
      this.reconcileSubscriptions(epoch).catch((error) => {
        if (epoch !== this.subscriptionEpoch) return;
        logError('live-journal', 'Subscription reconcile failed, retrying', error);
        this.scheduleSubscriptionReconcile();
      });
    }, delay);
  }

  // Refreshes the manifest and resubscribes whatever the dedupe maps no longer hold. Subscribing
  // while the cable is down is fine: AnyCable keeps the intent and sends it on the next connect.
  async reconcileSubscriptions(epoch) {
    await this.loadManifest();
    if (epoch !== this.subscriptionEpoch) return;

    // A lost stream the manifest no longer lists is gone for good (access revoked, channel
    // deleted); stop waiting on it so the backoff can settle.
    const listed = new Set(this.manifest.map((entry) => this.subscriptionSlotFor(entry).trackKey));
    for (const key of this.lostSubscriptions.keys()) {
      if (!listed.has(key)) this.lostSubscriptions.delete(key);
    }

    await this.subscribeToManifestChannels();
    if (epoch !== this.subscriptionEpoch) return;
    this.reportLostSubscriptions();
    if (this.lostSubscriptions.size === 0) this.subscriptionReconcileAttempts = 0;
    log('live-journal', `Reconciled live journal subscriptions; ${this.lostSubscriptions.size} awaiting repair`);
  }

  // One Sentry event per batch of losses, emitted once the resubscribe has actually been issued so a
  // failed pass keeps the batch for its retry. A key is reported once per loss.
  reportLostSubscriptions() {
    const unreported = [...this.lostSubscriptions.entries()].filter(([, entry]) => !entry.reported);
    if (unreported.length === 0) return;
    for (const [, entry] of unreported) entry.reported = true;
    const reasons = [...new Set(unreported.map(([, entry]) => entry.reason))].sort();
    Sentry.captureMessage('Live journal subscriptions closed unexpectedly; reconciled', {
      level: 'warning',
      extra: { count: unreported.length, reasons, keys: unreported.slice(0, 20).map(([key]) => key) },
      fingerprint: ['live-journal-subscriptions-lost', ...reasons],
    });
  }

  // Teardown: fences any in-flight reconcile and drops all lost-stream state.
  cancelSubscriptionReconcile() {
    this.subscriptionEpoch += 1;
    this.subscriptionsActive = false;
    if (this.subscriptionReconcileTimer) {
      clearTimeout(this.subscriptionReconcileTimer);
      this.subscriptionReconcileTimer = null;
    }
    this.subscriptionReconcileAttempts = 0;
    this.lostSubscriptions.clear();
  }

  // === live event handling ===

  async handleLiveJournalEvent(event, resourceType) {
    switch (resourceType) {
      case 'board_item':
        await this.handleLiveBoardItemEvent(event);
        break;
      case 'emoji':
        await this.handleLiveEmojiEvent(event);
        break;
      case 'widget':
        await this.handleLiveWidgetEvent(event);
        break;
      case 'message':
        await this.handleLiveMessageEvent(event);
        break;
      case 'channel':
        await this.handleLiveChannelEvent(event);
        break;
      case 'profile':
        await this.handleLiveProfileEvent(event);
        break;
      case 'mention':
        await this.handleLiveMentionEvent(event);
        break;
      case 'activity':
        await this.handleLiveActivityEvent(event);
        break;
      case 'agent_chat':
        await this.handleLiveAgentChatEvent(event);
        break;
      case 'channel_read_marker':
        await this.handleLiveChannelReadMarkerEvent(event);
        break;
      case 'message_reaction':
        await this.handleLiveMessageReactionEvent(event);
        break;
      case 'workspace':
        await this.handleLiveWorkspaceEvent(event);
        break;
      case 'user':
        await this.handleLiveUserEvent(event);
        break;
      case 'direct_message':
        await this.handleLiveDirectMessageEvent(event);
        break;
      case 'workspace_membership':
        await this.handleLiveWorkspaceMembershipEvent(event);
        break;
      default:
        logWarn('live-journal', `Unhandled live event type: ${resourceType}`);
    }
  }

  async handleLiveDirectMessageEvent(event) {
    const directMessage = event?.data?.direct_message;
    if (!directMessage?.id) return;

    const previous = await db.direct_messages.get(String(directMessage.id));
    await storeDirectMessagePayload(directMessage);
    this.invalidateDirectMessageWorkspaceIdCache();
    // Live event mutates one DM row, so the next bulk `loadDirectMessages` snapshot
    // will differ from whatever fingerprint we last stored.
    await clearFingerprint('direct_messages');

    // Routine `last_message_sort_order` bumps don't change the manifest scope, so skip
    // the heavy manifest+bootstrap refresh and let the workspace journal stream deliver
    // the actual message. Only refresh when the DM's workspace is new to us or visibility
    // flipped on (first-message-from-peer / un-hide).
    const isNewWorkspace = !previous || String(previous.workspace_id) !== String(directMessage.workspace_id);
    const becameVisible = directMessage.visible === true && previous?.visible !== true;
    if (!isNewWorkspace && !becameVisible) return;

    await this.loadManifest();
    await this.subscribeToManifestChannels();
    await this.loadRailMessageBootstrapForChannels([directMessage.main_channel_id]);
  }

  async handleLiveWorkspaceMembershipEvent(event) {
    const membership = event?.data?.workspace_membership;
    if (!membership?.workspace_id) return;

    if (membership.active === false) await this.killWorkspace(membership.workspace_id);

    await this.loadAuthenticatedShellBootstrap();
    await this.loadManifest();
    await this.subscribeToManifestChannels();
  }

  async refreshWorkspaceMembershipsForCatchup() {
    const workspaceId = currentWorkspaceId();
    const knownWorkspaceIds = await db.workspaces.toCollection().primaryKeys();

    await this.loadWorkspaces({ preserveCurrentWorkspaceRows: false });

    // Membership loss has to tear the whole workspace down, not just drop the rail row.
    // Scoped channel/profile rows would otherwise survive, and with them the channel
    // `deleting` hook that unsubscribes SignalJournalChannel — leaving journal traffic
    // flowing for a workspace we no longer belong to. killWorkspace only redirects when
    // the workspace is the current one, which the visibility check below handles instead.
    let remainingWorkspaceIds = new Set(await db.workspaces.toCollection().primaryKeys());
    const hasAddedWorkspace = [...remainingWorkspaceIds].some((candidateId) => !knownWorkspaceIds.includes(candidateId));
    if (hasAddedWorkspace) {
      // `/workspaces` proves membership but does not carry the profile/channel identity
      // rows RouteWorkspaceProvider needs. Hydrate that authoritative shell once, then
      // recalculate the membership snapshot before the caller refreshes the manifest.
      await this.loadAuthenticatedShellBootstrap();
      remainingWorkspaceIds = new Set(await db.workspaces.toCollection().primaryKeys());
    }

    for (const staleWorkspaceId of knownWorkspaceIds) {
      if (remainingWorkspaceIds.has(staleWorkspaceId)) continue;
      if (String(staleWorkspaceId) === String(workspaceId)) continue;
      await this.killWorkspace(String(staleWorkspaceId));
    }

    if (workspaceId && !(await hasVisibleWorkspace(db, workspaceId))) {
      await this.killWorkspace(workspaceId);
      return false;
    }

    return true;
  }

  async handleLiveEmojiEvent(event) {
    const { action, data } = event;
    if (!data?.emoji) return;

    // Invalidate fingerprint so next loadEmojis() does a full write
    await this.clearCollectionFingerprint('emojis');

    switch (action) {
      case 'create':
      case 'update':
        await db.emojis.put(data.emoji);
        break;

      case 'destroy':
        await db.emojis
          .where('[workspace_id+id]')
          .equals([String(data.emoji.workspace_id), String(data.emoji.id)])
          .delete();
        break;
    }
  }

  async handleLiveBoardItemEvent(event) {
    const { action, data } = event;
    if (!data?.board_item) return;

    await clearFingerprint('board_items');

    switch (action) {
      case 'create':
      case 'update':
        await db.board_items.put(data.board_item);
        break;

      case 'destroy':
        await db.board_items
          .where('[workspace_id+id]')
          .equals([String(data.board_item.workspace_id), String(data.board_item.id)])
          .delete();
        break;
    }
  }

  async handleLiveWidgetEvent(event) {
    const { action, data } = event;
    if (!data?.widget) return;

    switch (action) {
      case 'create':
      case 'update':
        await clearFingerprint('widgets');
        if (this.isSharedChannel) await this.loadWidgets();
        else await this.loadWidget(data.widget.id);
        break;

      case 'destroy':
        await clearFingerprint('widgets');
        await db.widgets
          .where('[workspace_id+id]')
          .equals([String(data.widget.workspace_id), String(data.widget.id)])
          .delete();
        break;
    }
  }

  async handleLiveMessageEvent(event) {
    const { action, cursor = null, data } = event;
    if (!data?.message) return;
    const workspaceId = data.message.workspace_id;
    if (!workspaceId) {
      logError('live-journal', 'Skipping live message without workspace_id', { action, message: data.message });
      return;
    }

    logDebug('live-journal', `✉️ [handleLiveMessageEvent] ${action} for ${data.message.sort_order}`);

    this.dispatchLiveMessageEvent(action, data.message, cursor);

    const existing = await messagesByChannelAndSortOrder(data.message.channel_id, data.message.sort_order).count();

    switch (action) {
      case 'create':
      case 'update':
        await this.batchInsertMessages([data.message]);
        break;
      case 'destroy':
        if (existing !== 0) {
          const keys = await messagesByChannelAndSortOrder(data.message.channel_id, data.message.sort_order).primaryKeys();
          if (keys.length) await db.messages.bulkDelete(keys);
        }
        break;
    }

    if (action === 'destroy') await syncWorkspaceLatestMessageChanges({ confirmedDeletes: [data.message] });
  }

  async handleLiveChannelEvent(event) {
    const { action, data } = event;
    if (!data?.channel) return;
    const channelWorkspaceId = data.channel.workspace_id;
    if (!channelWorkspaceId) return;

    switch (action) {
      case 'create':
      case 'update':
        if (this.isSharedChannel) {
          await this.clearCollectionFingerprint('channels');
          if (!this.pendingSharedChannelReload) {
            this.pendingSharedChannelReload = true;
            this.loadChannels(false).finally(() => (this.pendingSharedChannelReload = false));
          }
        } else {
          await this.clearCollectionFingerprint('channels');
          await db.transaction('rw', db.channels, async () => {
            await db.channels.delete([channelWorkspaceId, `skeleton_${data.channel.id}`]);
            await db.channels.put(data.channel);
          });
          await this.loadManifest();
          await this.subscribeToChannelCollections(data.channel.id);
        }
        break;

      case 'destroy':
        // purge the workspace if the initial channel was a shared channel and its been destroyed
        if (this.isSharedChannel && data.channel.id === this.initialChannelIdValue) {
          await this.killWorkspace(currentWorkspaceId());
          return;
        }

        await this.clearCollectionFingerprint('channels');
        await db.channels.bulkDelete([
          [channelWorkspaceId, `skeleton_${data.channel.id}`],
          [channelWorkspaceId, data.channel.id],
        ]);
        this.unsubscribeFromChannel(data.channel.id);
        break;
    }
  }

  async handleLiveProfileEvent(event) {
    const { action, data } = event;
    if (!data?.profile) return;

    await this.clearCollectionFingerprint('profiles');

    switch (action) {
      case 'create':
      case 'update':
        await db.profiles.put(data.profile);
        break;

      case 'destroy':
        await db.profiles.delete([data.profile.workspace_id, data.profile.id]);
        break;
    }
  }

  async handleLiveMentionEvent(event) {
    const { action, data } = event;
    if (!data?.mention) {
      return;
    }

    await clearFingerprint('mentions');

    switch (action) {
      case 'create':
      case 'update':
        await db.mentions.put(data.mention);
        await this.removeMentionSkeletonTwins([data.mention]);
        break;

      case 'destroy':
        await db.mentions.delete([data.mention.workspace_id, data.mention.id]);
        break;
    }
  }

  async handleLiveActivityEvent(event) {
    const { action, data } = event;
    if (action === 'read_all') {
      await this.handleLiveActivityReadAllEvent(data);
      return;
    }

    if (!data?.activity) return;

    await clearFingerprint('activities');

    switch (action) {
      case 'create':
      case 'update':
        await db.activities.put(data.activity);
        break;

      case 'destroy':
        await db.activities.delete(data.activity.id);
        break;
    }
  }

  async handleLiveActivityReadAllEvent(data) {
    const workspaceId = data?.workspace_id ? String(data.workspace_id) : null;
    if (!workspaceId) return;

    const recipientUserId = data?.recipient_user_id ? String(data.recipient_user_id) : null;
    const readAt = data?.read_at || Math.floor(Date.now() / 1000);
    const unreadRows = await db.activities
      .where('workspace_id')
      .equals(workspaceId)
      .filter((activity) => {
        if (activity.read_at != null) return false;
        if (recipientUserId && String(activity.recipient_user_id) !== recipientUserId) return false;
        return true;
      })
      .toArray();

    await clearFingerprint('activities');
    if (unreadRows.length === 0) return;

    await db.activities.bulkPut(unreadRows.map((activity) => ({ ...activity, read_at: readAt, updated_at: readAt })));
  }

  async handleLiveAgentChatEvent(event) {
    const { action, data } = event;
    if (!data?.agent_chat) return;

    await clearFingerprint('agent_chats');

    switch (action) {
      case 'create':
      case 'update':
        await storeAgentChatWithGenerationTurn(data.agent_chat);
        break;

      case 'destroy':
        await db.agent_chats
          .where('[workspace_id+id]')
          .equals([String(data.agent_chat.workspace_id), String(data.agent_chat.id)])
          .delete();
        break;
    }
  }

  async handleLiveChannelReadMarkerEvent(event) {
    const { action, data } = event;
    const marker = data?.channel_read_marker;
    if (!marker) return;

    // WARNING: always use the PK [workspace_id+id]. Do NOT switch to
    // .where('[workspace_id+channel_id]').delete() — that nukes every profile's
    // marker for the channel, which breaks multi-identity users.
    if (action === 'destroy') {
      await db.channel_read_markers.delete([marker.workspace_id, marker.id]);
      return;
    }

    await putMergedChannelReadMarker(marker);
  }

  async handleLiveMessageReactionEvent(event) {
    const { action, data } = event;
    if (!data?.message_reaction) return;

    const reaction = data.message_reaction;
    const workspaceId = reaction.workspace_id;
    if (!workspaceId) {
      logError('live-journal', 'Skipping live message reaction without workspace_id', { action, reaction });
      return;
    }

    // for create/update: check if this reaction was cancelled before processing
    if (action === 'create' || action === 'update') {
      // check by optimistic_sort_order if present
      if (reaction.optimistic_sort_order && this.cancelledReactionSubmissions.has(reaction.optimistic_sort_order)) {
        logDebug('live-journal', 'Ignoring cancelled reaction from server', { optimistic_sort_order: reaction.optimistic_sort_order });
        // clean up cancelled entry immediately
        this.cancelledReactionSubmissions.delete(reaction.optimistic_sort_order);
        return;
      }

      // if optimistic_sort_order is present, use it to find and remove the matching optimistic reaction
      if (reaction.optimistic_sort_order) {
        const message = await messageById(reaction.message_id);
        if (message?.reactions) {
          // find optimistic reaction with matching optimistic_sort_order
          const optimisticReaction = Object.values(message.reactions).find(
            (r) => r.pseudotype === 'optimistic' && r.optimistic_sort_order === reaction.optimistic_sort_order,
          );

          if (optimisticReaction) {
            // remove the optimistic reaction (normal flow)
            await db.messages
              .where('id')
              .equals(reaction.message_id)
              .modify((msg) => {
                if (msg.reactions && msg.reactions[optimisticReaction.id]) {
                  delete msg.reactions[optimisticReaction.id];
                }
              });
          }
        }
      }
    }

    // Dispatch event to the message feed for live UI updates
    window.dispatchEvent(new CustomEvent(LIVE_REACTION_EVENT, { detail: { action, data: reaction } }));

    switch (action) {
      case 'create':
      case 'update':
        // Use Dexie's nested object modification to add/update a reaction
        await db.messages
          .where('id')
          .equals(reaction.message_id)
          .modify({ [`reactions.${reaction.id}`]: reaction });
        break;

      case 'destroy':
        // Use Dexie's nested object modification to remove a reaction
        await db.messages
          .where('id')
          .equals(reaction.message_id)
          .modify({ [`reactions.${reaction.id}`]: undefined });
        break;
    }
  }

  async handleLiveWorkspaceEvent(event) {
    const { action, data } = event;
    if (!data?.workspace) return;

    switch (action) {
      case 'create':
      case 'update':
        await db.workspaces.put(data.workspace);
        break;

      case 'destroy':
        await this.killWorkspace(data.workspace.id);
        break;
    }
  }

  async handleLiveUserEvent(event) {
    const { action, data } = event;
    if (!data?.user) return;

    switch (action) {
      case 'create':
      case 'update':
        await this.storeUser(data.user);
        break;
      case 'destroy':
        await db.users.delete(data.user.id);
        break;
    }
  }

  addManifestCollections(collections) {
    if (!Array.isArray(collections) || collections.length === 0) return;

    const existingKeys = new Set(this.manifest.map((entry) => this.manifestCollectionKey(entry)));
    const newCollections = collections.filter((entry) => {
      const key = this.manifestCollectionKey(entry);
      if (existingKeys.has(key)) return false;

      existingKeys.add(key);
      return true;
    });

    if (newCollections.length > 0) this.manifest = [...this.manifest, ...newCollections];
    this.profileIds = new Set(this.manifest.filter((entry) => entry.scope === 'profile').map((entry) => entry.collection_name));
  }

  manifestCollectionKey(entry) {
    return this.composite(entry.user_id || '', entry.workspace_id || '', entry.scope || '', entry.collection_name || '', entry.reference_kind || '');
  }

  async hydrateCreatedWorkspace(payload = {}) {
    const { user, workspace, profile, channel } = payload;
    if (!workspace?.id) return;

    if (user) await this.storeShellUser(user);
    await db.workspaces.put(workspace);
    if (profile?.id && profile?.workspace_id) await this.batchInsertProfiles([profile]);
    if (channel?.id && channel?.workspace_id) await this.batchInsertChannels([channel]);

    await clearFingerprint('workspaces');
    await this.clearCollectionFingerprint('profiles');
    await this.clearCollectionFingerprint('channels');

    const journalCollections = payload.journal_collections || [];
    if (journalCollections.length > 0) {
      this.addManifestCollections(journalCollections);
    } else {
      await this.loadManifest();
    }
    await this.subscribeToManifestChannels();
  }

  // === data loading methods ===

  async loadRootData() {
    let initialMessageBootstrapPromise;
    let initialWorkspacesPromise;
    let initialDirectMessagesPromise;
    let initialRailSourcesPromise;

    // Load DM metadata first so the standard global loaders can preserve DM-scoped
    // rows that intentionally do not appear in normal workspace snapshots. Start
    // the independent rail requests immediately so they do not wait on that round trip.
    if (!this.isSharedChannel) {
      initialMessageBootstrapPromise = this.loadMessageBootstrap({ representation: 'rail' });
      initialWorkspacesPromise = this.loadWorkspaces();
      initialDirectMessagesPromise = this.loadDirectMessages({ representation: 'rail' });
      initialRailSourcesPromise = Promise.all([initialWorkspacesPromise, initialDirectMessagesPromise]).then(
        markWorkspaceRailBootstrapReady,
      );
      await Promise.all([this.loadMe(), initialDirectMessagesPromise]);
    }

    const methods = [
      this.isSharedChannel ? this.loadMe() : null,
      initialWorkspacesPromise || this.loadWorkspaces(),
      this.loadChannels(false),
      this.loadProfiles(),
      this.loadWidgets(),
      this.loadBoardItems(),
      this.loadEmojis(),
    ];

    if (!this.isSharedChannel) {
      methods.push(initialMessageBootstrapPromise);
      methods.push(this.loadAgentChats());
      methods.push(this.loadMentions());
      methods.push(this.loadActivities());
      methods.push(initialRailSourcesPromise);
    }

    await Promise.all(methods.filter(Boolean));
    if (this.isSharedChannel) await this.loadMessageBootstrap();
    // Keep public-room reads shadow-scoped while warming signed-in shell data as the real user.
    if (this.isSharedChannel && currentUserId()) await this.loadSignedInSharedShellData();
    if (this.isSharedChannel) markWorkspaceRailBootstrapReady();
  }

  async loadSignedInSharedShellData() {
    try {
      await this.loadAuthenticatedShellBootstrap({ asUser: true, preserveCurrentWorkspaceRows: true });
    } catch (error) {
      logError('live-journal', 'Failed to load signed-in shared shell bootstrap', error);
    }
  }

  async loadAuthenticatedShellBootstrap({ asUser = false, preserveCurrentWorkspaceRows = false } = {}) {
    await runLoaderWithRetries('loadAuthenticatedShellBootstrap', async () => {
      const { response, data } = await apiRecoveryRead('/shell/bootstrap', { asUser });

      if (!(response.ok && data.ok)) {
        throw new Error(
          `Failed to fetch shell bootstrap: ${(Array.isArray(data.errors) ? data.errors : []).join(', ') || 'Unknown error'}`,
        );
      }

      await this.storeAuthenticatedShellBootstrap(data.data || {}, {
        preserveCurrentWorkspaceRows,
        userAuthoritative: asUser || !this.isSharedChannel,
      });
      log('live-journal', 'Loaded authenticated shell bootstrap');
    });
  }

  async storeAuthenticatedShellBootstrap(payload, { preserveCurrentWorkspaceRows = false, userAuthoritative = false } = {}) {
    await this.storeShellUser(payload.user, payload.configs);
    await authorizeWorkspaceWidgetBoardsFromMembershipSnapshot(payload.workspaces || [], { userAuthoritative });
    await this.storeShellWorkspaceSnapshot(payload.workspaces || [], { preserveCurrentWorkspaceRows, userAuthoritative });

    const profiles = payload.profiles || [];
    if (profiles.length > 0) await this.batchInsertProfiles(profiles);

    const channels = payload.channels || [];
    if (channels.length > 0) await this.batchInsertChannels(channels);

    const directMessages = payload.direct_messages || [];
    await replaceVisibleDirectMessages(directMessages);
    this.invalidateDirectMessageWorkspaceIdCache();
    await this.pruneShellScopedRows({ profiles, channels, preserveCurrentWorkspaceRows });

    const readMarkers = payload.channel_read_markers || [];
    await this.batchInsertChannelReadMarkers(readMarkers);

    const skeletons = payload.read_marker_skeletons || {};
    await this.bulkApplyReadMarkerMessageSkeletons(skeletons.create_message || [], skeletons.remove_message || []);

    const messages = payload.messages || [];
    const shellWorkspaceIds = [
      ...(payload.workspaces || []).map((workspace) => workspace.id),
      ...(payload.direct_messages || []).map((directMessage) => directMessage.workspace_id),
    ];
    await replaceWorkspaceLatestMessages(messages, { workspaceIds: shellWorkspaceIds });
    if (messages.length > 0) await this.batchInsertMessages(messages, { syncWorkspaceProjection: false });

    const mentions = payload.mentions || [];
    if (mentions.length > 0) {
      await db.mentions.bulkPut(mentions);
      await this.removeMentionSkeletonTwins(mentions);
    }
    await this.bulkApplyReadMarkerMentionSkeletons(skeletons.create_mention || [], skeletons.remove_mention || []);

    const activities = payload.activities || [];
    if (activities.length > 0) await db.activities.bulkPut(activities);

    // On shared-channel pages the cable connects as the shadow, so the signed-in user's
    // manifest cannot be subscribed here. Main-app callers refresh it through loadManifest().
  }

  async storeShellUser(user, configs = null) {
    if (user) {
      await this.storeUser(user);

      const existingIds = await db.users.toCollection().primaryKeys();
      const staleIds = existingIds.filter((id) => String(id) !== String(user.id));
      if (staleIds.length > 0) await db.users.bulkDelete(staleIds);
    } else {
      await db.users.clear();
    }

    const letters = configs?.glyphs?.letters;
    if (Array.isArray(letters) && letters.length) sessionStorage.setItem('output:glyphs:letters', JSON.stringify(letters));
  }

  async storeUser(user) {
    if (!user?.id) return;

    const updatedCount = await db.users.update(String(user.id), user);
    if (updatedCount === 0) await db.users.add(user);
  }

  async storeShellWorkspaceSnapshot(workspaces, { preserveCurrentWorkspaceRows = false, userAuthoritative = false } = {}) {
    if (workspaces.length > 0) await db.workspaces.bulkPut(workspaces);

    const staleWorkspaceIds = await pruneStaleWorkspaces(db, workspaces, {
      preserveWorkspaceId: preserveCurrentWorkspaceRows ? currentWorkspaceId() : null,
    });
    if (userAuthoritative) {
      for (const staleWorkspaceId of staleWorkspaceIds) await revokeWorkspaceWidgetBoard(staleWorkspaceId);
    }
  }

  async pruneShellScopedRows({ profiles, channels, preserveCurrentWorkspaceRows = false }) {
    const directWorkspaceIds = await this.directMessageWorkspaceIds();
    const preserveWorkspaceIds = new Set(directWorkspaceIds);
    const workspaceId = currentWorkspaceId();
    if (preserveCurrentWorkspaceRows && workspaceId) preserveWorkspaceIds.add(String(workspaceId));

    await this.pruneShellScopedSnapshotTable(db.profiles, profiles, preserveWorkspaceIds);
    await this.pruneShellScopedSnapshotTable(db.channels, channels, preserveWorkspaceIds);
  }

  async pruneShellScopedSnapshotTable(table, rows, preserveWorkspaceIds) {
    const keepKeys = new Set((rows || []).map((row) => this.composite(row.workspace_id, row.id)));
    const existingRows = await table.toArray();
    const deleteKeys = existingRows
      .filter(
        (row) =>
          row.workspace_id != null &&
          row.id != null &&
          !preserveWorkspaceIds.has(String(row.workspace_id)) &&
          !keepKeys.has(this.composite(row.workspace_id, row.id)),
      )
      .map((row) => [row.workspace_id, row.id]);

    if (deleteKeys.length > 0) await table.bulkDelete(deleteKeys);
  }

  async loadDirectMessages({ representation = 'rail' } = {}) {
    if (this.isSharedChannel) return [];

    return runLoaderWithRetries('loadDirectMessages', async () => {
      const { response, data } = await apiRecoveryRead(`/direct_messages?representation=${encodeURIComponent(representation)}`);

      if (!(response.ok && data.ok)) {
        throw new Error(`Failed to fetch direct messages: ${(Array.isArray(data.errors) ? data.errors : []).join(', ') || 'Unknown error'}`);
      }

      const directMessages = data.data.direct_messages || [];

      const { changed, hash } = await hasDataChanged('direct_messages', directMessages);
      if (!changed) {
        log('live-journal', `Skipped direct messages bulkPut — fingerprint unchanged (${directMessages.length} direct messages)`);
        return [];
      }

      const normalized = await replaceVisibleDirectMessages(directMessages);
      this.invalidateDirectMessageWorkspaceIdCache();
      await storeFingerprint('direct_messages', hash);
      log('live-journal', `Loaded ${normalized.length} direct messages`);
      return normalized;
    });
  }

  async refreshDirectMessagesAndManifest() {
    if (this.isSharedChannel) {
      if (currentUserId()) await this.loadSignedInSharedShellData();
      return;
    }

    await this.loadDirectMessages();
    await this.loadManifest();
    await this.subscribeToManifestChannels();

    const directMessages = await db.direct_messages.filter((directMessage) => directMessage.visible !== false).toArray();
    await this.loadRailMessageBootstrapForChannels(directMessages.map((directMessage) => directMessage.main_channel_id));
  }

  async loadRailMessageBootstrapForChannels(channelIds) {
    const normalizedChannelIds = [...new Set(channelIds.filter(Boolean).map(String))];
    for (let offset = 0; offset < normalizedChannelIds.length; offset += MESSAGE_BOOTSTRAP_MAX_CHANNELS) {
      await this.loadMessageBootstrap({
        channelIds: normalizedChannelIds.slice(offset, offset + MESSAGE_BOOTSTRAP_MAX_CHANNELS),
        replace: false,
        force: true,
      });
    }
  }

  loadMessageBootstrap({ representation = this.isSharedChannel ? 'full' : 'rail', channelIds = null, replace = channelIds == null, force = false } = {}) {
    const normalizedChannelIds = channelIds ? [...new Set(channelIds.filter(Boolean).map(String))].sort() : null;
    const requestScope = normalizedChannelIds == null ? 'all' : `channels:${normalizedChannelIds.join(',') || 'none'}`;
    const requestKey = `${representation}:${requestScope}`;
    const freshnessEligible = representation === 'rail' && normalizedChannelIds == null;
    if (freshnessEligible && !force && Date.now() - this.railMessageBootstrapCompletedAt < RAIL_MESSAGE_BOOTSTRAP_FRESHNESS_MS) {
      return Promise.resolve(false);
    }

    const inFlight = this.messageBootstrapRequests.get(requestKey);
    if (inFlight) return inFlight;

    const request = this.performMessageBootstrap({ representation, channelIds: normalizedChannelIds, replace })
      .then((loaded) => {
        if (loaded && freshnessEligible) this.railMessageBootstrapCompletedAt = Date.now();
        return loaded;
      })
      .finally(() => this.messageBootstrapRequests.delete(requestKey));
    this.messageBootstrapRequests.set(requestKey, request);
    return request;
  }

  async performMessageBootstrap({ representation, channelIds, replace }) {
    try {
      const query = new URLSearchParams({ representation });
      if (channelIds) {
        query.set('channel_id', channelIds.join(','));
        query.set('per_channel', '3');
      }
      const { response, data } = await apiRecoveryRead(`/messages/bootstrap?${query.toString()}`);

      if (!(response.ok && data.ok)) {
        const errors = Array.isArray(data.errors) ? data.errors : [];
        throw new Error(`Failed to fetch message bootstrap: ${errors.join(', ') || 'Unknown error'}`);
      }

      const messages = data.data?.messages || [];
      if (replace) {
        const workspaceIds = this.isSharedChannel ? [currentWorkspaceId()] : null;
        await replaceWorkspaceLatestMessages(messages, { workspaceIds });
      } else {
        await syncWorkspaceLatestMessageChanges({ upserts: messages });
      }

      if (representation === 'rail') {
        const conferenceTranscripts = messages.filter((message) => message.type === 'conference_transcript');
        if (conferenceTranscripts.length > 0) await this.messageBulkMerge(conferenceTranscripts);
      } else {
        // Route through the normal message writer so skeleton cleanup, optimistic
        // reconciliation, mentions, reactions, and attachment snapshots behave
        // exactly like channel history fetches. The projection was already updated above.
        await this.batchInsertMessages(messages, { syncWorkspaceProjection: false });
      }
      log('live-journal', `Bootstrapped ${messages.length} recent messages (${representation})`);
      return true;
    } catch (error) {
      // Workspace rail previews are opportunistic cache warming. A bootstrap miss
      // should not block the app shell or force a reload.
      logError('live-journal', 'Failed to bootstrap recent messages', error);
      return false;
    }
  }

  async loadEmojis() {
    await runLoaderWithRetries('loadEmojis', async () => {
      const { response, data } = await apiRecoveryRead('/emojis');

      if (!(response.ok && data.ok)) {
        throw new Error(`Failed to fetch emojis: ${(Array.isArray(data.errors) ? data.errors : []).join(', ') || 'Unknown error'}`);
      }

      const emojis = data.data.emojis || [];

      const fingerprintKey = this.collectionFingerprintKey('emojis');
      const { changed, hash } = await hasDataChanged(fingerprintKey, emojis);
      if (!changed) {
        await storeFingerprint(fingerprintKey, hash);
        log('live-journal', `Skipped emojis bulkPut — fingerprint unchanged (${emojis.length} emojis)`);
        return;
      }

      // full API snapshot: drop dexie rows missing from this list
      const keepKeys = new Set(emojis.map((e) => this.composite(e.workspace_id, e.id)));
      const existing = await db.emojis.toArray();
      const keysToDelete = existing
        .filter((row) => row.workspace_id != null && row.id != null && !keepKeys.has(this.composite(row.workspace_id, row.id)))
        .map((row) => [row.workspace_id, row.id]);
      if (keysToDelete.length > 0) {
        await db.emojis.bulkDelete(keysToDelete);
        logDebug('live-journal', `Pruned ${keysToDelete.length} emoji row(s) not in API snapshot`);
      }

      await db.emojis.bulkPut(emojis);
      await storeFingerprint(fingerprintKey, hash);
      log('live-journal', `Loaded ${emojis.length} emojis`);
    });
  }

  async loadMentions() {
    if (this.isSharedChannel) throw new Error('loadMentions() is not supported in shared channel mode');

    await runLoaderWithRetries('loadMentions', async () => {
      const { response, data } = await apiRecoveryRead('/mentions');

      if (!(response.ok && data.ok)) {
        throw new Error(`Failed to fetch mentions: ${(Array.isArray(data.errors) ? data.errors : []).join(', ') || 'Unknown error'}`);
      }

      const mentions = data.data.mentions || [];

      const { changed, hash } = await hasDataChanged('mentions', mentions);
      if (!changed) {
        log('live-journal', `Skipped mentions bulkPut — fingerprint unchanged (${mentions.length} mentions)`);
        return;
      }

      await db.mentions.bulkPut(mentions);
      await this.removeMentionSkeletonTwins(mentions);

      // The endpoint returns only the newest page of mentions, so absence proves
      // deletion only within that window. Older rows (an unread mention that aged past
      // the cap) and read-marker skeleton rows must survive the prune or their unread
      // badges vanish.
      const oldestFetchedSortOrder = mentions.length > 0 ? mentions[mentions.length - 1].sort_order : null;
      const keepKeys = new Set(mentions.map((mention) => this.composite(mention.workspace_id, mention.id)));
      const directWorkspaceIds = await this.directMessageWorkspaceIds();
      const existing = await db.mentions.toArray();
      const keysToDelete = existing
        .filter(
          (row) =>
            row.workspace_id != null &&
            row.id != null &&
            !String(row.id).startsWith('skeleton_') &&
            (oldestFetchedSortOrder == null || String(row.sort_order) >= oldestFetchedSortOrder) &&
            !directWorkspaceIds.has(String(row.workspace_id)) &&
            !keepKeys.has(this.composite(row.workspace_id, row.id)),
        )
        .map((row) => [row.workspace_id, row.id]);
      if (keysToDelete.length > 0) await db.mentions.bulkDelete(keysToDelete);

      await storeFingerprint('mentions', hash);
      log('live-journal', `Loaded ${mentions.length} mentions`);
    });
  }

  async loadActivities() {
    if (this.isSharedChannel) throw new Error('loadActivities() is not supported in shared channel mode');

    await runLoaderWithRetries('loadActivities', async () => {
      const { response, data } = await apiRecoveryRead('/activities?limit=200');

      if (!(response.ok && data.ok)) {
        throw new Error(`Failed to fetch activities: ${(Array.isArray(data.errors) ? data.errors : []).join(', ') || 'Unknown error'}`);
      }

      const activities = data.data.activities || [];

      const { changed, hash } = await hasDataChanged('activities', activities);
      if (!changed) {
        log('live-journal', `Skipped activities bulkPut — fingerprint unchanged (${activities.length} activities)`);
        return;
      }

      await db.activities.bulkPut(activities);

      await storeFingerprint('activities', hash);
      log('live-journal', `Loaded ${activities.length} activities`);
    });
  }

  async loadBoardItems() {
    await runLoaderWithRetries('loadBoardItems', async () => {
      const { response, data } = await apiRecoveryRead('/board_items');

      if (!(response.ok && data.ok)) {
        throw new Error(`Failed to fetch board items: ${(Array.isArray(data.errors) ? data.errors : []).join(', ') || 'Unknown error'}`);
      }

      const boardItems = data.data.board_items || [];

      const { changed, hash } = await hasDataChanged('board_items', boardItems);
      if (!changed) {
        log('live-journal', `Skipped board_items bulkPut — fingerprint unchanged (${boardItems.length} board items)`);
        return;
      }

      await db.board_items.bulkPut(boardItems);

      const keepKeys = new Set(boardItems.map((boardItem) => this.composite(boardItem.workspace_id, boardItem.id)));
      const scopedWorkspaceId = this.isSharedChannel ? currentWorkspaceId() : null;
      const directWorkspaceIds = await this.directMessageWorkspaceIds();
      const existing = await db.board_items.toArray();
      const keysToDelete = existing
        .filter((row) => {
          if (row.workspace_id == null || row.id == null) return false;
          if (scopedWorkspaceId && row.workspace_id !== scopedWorkspaceId) return false;
          // DM board rows are loaded through DM route/journal paths, not the global board endpoint.
          if (!scopedWorkspaceId && directWorkspaceIds.has(String(row.workspace_id))) return false;
          return !keepKeys.has(this.composite(row.workspace_id, row.id));
        })
        .map((row) => [row.workspace_id, row.id]);
      if (keysToDelete.length > 0) await db.board_items.bulkDelete(keysToDelete);

      await storeFingerprint('board_items', hash);
      log('live-journal', `Loaded ${boardItems.length} board items`);
    });
  }

  async loadWidgets() {
    await runLoaderWithRetries('loadWidgets', async () => {
      const { response, data } = await apiRecoveryRead('/widgets');

      if (!(response.ok && data.ok)) {
        throw new Error(`Failed to fetch widgets: ${(Array.isArray(data.errors) ? data.errors : []).join(', ') || 'Unknown error'}`);
      }

      const widgets = data.data.widgets || [];

      const { changed, hash } = await hasDataChanged('widgets', widgets);
      if (!changed) {
        log('live-journal', `Skipped widgets bulkPut — fingerprint unchanged (${widgets.length} widgets)`);
        return;
      }

      await db.widgets.bulkPut(widgets);

      const keepKeys = new Set(widgets.map((widget) => this.composite(widget.workspace_id, widget.id)));
      const directWorkspaceIds = await this.directMessageWorkspaceIds();
      const existing = await db.widgets.toArray();
      const keysToDelete = existing
        .filter(
          (row) =>
            row.workspace_id != null &&
            row.id != null &&
            // DM widgets are loaded through DM route/journal paths, not the global widget endpoint.
            !directWorkspaceIds.has(String(row.workspace_id)) &&
            !keepKeys.has(this.composite(row.workspace_id, row.id)),
        )
        .map((row) => [row.workspace_id, row.id]);
      if (keysToDelete.length > 0) await db.widgets.bulkDelete(keysToDelete);

      await storeFingerprint('widgets', hash);
      log('live-journal', `Loaded ${widgets.length} widgets`);
    });
  }

  async loadProfiles() {
    await runLoaderWithRetries('loadProfiles', async () => {
      const { response, data } = await apiRecoveryRead('/profiles');

      if (!(response.ok && data.ok)) {
        throw new Error(`Failed to fetch profiles: ${(Array.isArray(data.errors) ? data.errors : []).join(', ') || 'Unknown error'}`);
      }

      const profiles = data.data.profiles || [];

      const fingerprintKey = this.collectionFingerprintKey('profiles');
      const { changed, hash } = await hasDataChanged(fingerprintKey, profiles);
      const cachedProfilesNeedRefresh = !changed && (await profileRowsNeedRefresh(db.profiles, profiles));
      if (!changed && !cachedProfilesNeedRefresh) {
        await storeFingerprint(fingerprintKey, hash);
        log('live-journal', `Skipped profiles bulkPut — fingerprint unchanged (${profiles.length} profiles)`);
        return;
      }
      if (cachedProfilesNeedRefresh) {
        log('live-journal', 'Refreshing incomplete cached profiles despite unchanged fingerprint');
      }

      await db.profiles.bulkPut(profiles);

      // full API snapshot (global_profiles): drop dexie rows missing from this list
      const keepKeys = new Set(profiles.map((p) => this.composite(p.workspace_id, p.id)));
      const directWorkspaceIds = await this.directMessageWorkspaceIds();
      const sharedWorkspaceId = this.isSharedChannel ? currentWorkspaceId() : null;
      const existing = await db.profiles.toArray();
      const keysToDelete = existing
        .filter(
          (row) =>
            row.workspace_id != null &&
            row.id != null &&
            (!this.isSharedChannel || String(row.workspace_id) === String(sharedWorkspaceId)) &&
            !directWorkspaceIds.has(String(row.workspace_id)) &&
            !keepKeys.has(this.composite(row.workspace_id, row.id)),
        )
        .map((row) => [row.workspace_id, row.id]);
      if (keysToDelete.length > 0) {
        await db.profiles.bulkDelete(keysToDelete);
        logDebug('live-journal', `Pruned ${keysToDelete.length} profile row(s) not in API snapshot`);
      }

      await storeFingerprint(fingerprintKey, hash);
      log('live-journal', `Loaded ${profiles.length} profiles`);
    });
  }

  async loadChannels(refreshManifest = true) {
    await runLoaderWithRetries('loadChannels', async () => {
      const { response, data } = await apiRecoveryRead('/channels');

      if (!(response.ok && data.ok)) {
        throw new Error(`Failed to fetch channels: ${(Array.isArray(data.errors) ? data.errors : []).join(', ') || 'Unknown error'}`);
      }

      const channels = data.data.channels || [];

      const channelsFingerprintKey = this.collectionFingerprintKey('channels');
      const { changed: channelsChanged, hash: channelsHash } = await hasDataChanged(channelsFingerprintKey, channels);
      if (!channelsChanged) {
        await storeFingerprint(channelsFingerprintKey, channelsHash);
        log('live-journal', `Skipped channels bulkPut — fingerprint unchanged (${channels.length} channels)`);
      } else {
        await this.batchInsertChannels(channels);
        await storeFingerprint(channelsFingerprintKey, channelsHash);
      }

      const keepKeys = new Set(channels.map((c) => this.composite(c.workspace_id, c.id)));
      const directWorkspaceIds = await this.directMessageWorkspaceIds();
      const scopedWorkspaceId = this.isSharedChannel ? currentWorkspaceId() : null;
      const existingChannels = await db.channels.toArray();
      const channelKeysToDelete = existingChannels
        .filter(
          (row) =>
            !String(row.id).startsWith('skeleton_') &&
            (!scopedWorkspaceId || String(row.workspace_id) === String(scopedWorkspaceId)) &&
            !directWorkspaceIds.has(String(row.workspace_id)) &&
            !keepKeys.has(this.composite(row.workspace_id, row.id)),
        )
        .map((row) => [row.workspace_id, row.id]);
      if (channelKeysToDelete.length > 0) {
        await db.channels.bulkDelete(channelKeysToDelete);
        logDebug('live-journal', `Pruned ${channelKeysToDelete.length} channel row(s) not in API snapshot`);
      }

      log('live-journal', `Loaded ${channels.length} channels`);

      // load read markers for all channels (once() avoids nested retry wrapper vs loadChannelReadMarkers)
      await this.loadChannelReadMarkersOnce();

      if (refreshManifest) {
        // reload manifest to get new collection keys
        await this.loadManifest();

        // subscribe to all channel collections
        for (const channel of channels) {
          await this.subscribeToChannelCollections(channel.id);
        }
      }
    });
  }

  // Cross-workspace: /api/v2/channel_read_markers returns every marker owned by the
  // current user plus unread skeletons for each, across all workspaces they belong to.
  //
  // Gap: channels with no CRM (never marked-as-read) don't appear here, so their
  // historical unread messages won't get skeleton rows. Live messages still stream in
  // via channel-scoped subscriptions; only pre-session history is invisible.
  // TODO: make the server auto-create a CRM on channel join, or extend this endpoint
  // to return skeletons for markerless channels using a day-zero cutoff.
  async loadChannelReadMarkersOnce() {
    const { response, data } = await apiRecoveryRead('/channel_read_markers');

    if (!(response.ok && data.ok)) {
      throw new Error(
        `Failed to fetch channel read markers: ${(Array.isArray(data.errors) ? data.errors : []).join(', ') || 'Unknown error'}`,
      );
    }

    const readMarkers = data.data.read_markers || [];
    log('skeletons', `Batch inserted ${readMarkers.length} channel read markers`);
    await this.batchInsertChannelReadMarkers(readMarkers);

    const sk = data.data.skeletons || {};
    await this.bulkApplyReadMarkerMessageSkeletons(sk.create_message || [], sk.remove_message || []);
    await this.bulkApplyReadMarkerMentionSkeletons(sk.create_mention || [], sk.remove_mention || []);
  }

  async loadChannelReadMarkers() {
    await runLoaderWithRetries('loadChannelReadMarkers', async () => {
      await this.loadChannelReadMarkersOnce();
    });
  }

  async loadAgentChats() {
    if (this.isSharedChannel) throw new Error('loadAgentChats() is not supported in shared channel mode');

    await runLoaderWithRetries('loadAgentChats', async () => {
      const { response, data } = await apiRecoveryRead('/agent_chats');

      if (!(response.ok && data.ok)) {
        throw new Error(`Failed to fetch agent chats: ${(Array.isArray(data.errors) ? data.errors : []).join(', ') || 'Unknown error'}`);
      }

      const chats = data.data.agent_chats || [];

      const { changed, hash } = await hasDataChanged('agent_chats', chats);
      if (!changed) {
        log('live-journal', `Skipped agent_chats bulkPut — fingerprint unchanged (${chats.length} chats)`);
        return;
      }

      // preserve locally-cached fields that aren't in the list payload (e.g. context_channel_id,
      // which is only populated by the detail endpoint)
      const currentAgentChats = await db.agent_chats.toArray();
      const existingByKey = new Map(currentAgentChats.map((chat) => [this.composite(chat.workspace_id, chat.id), chat]));
      const nextChats = chats.map((chat) =>
        mergeAgentChatWithGenerationTurn(existingByKey.get(this.composite(chat.workspace_id, chat.id)), chat),
      );

      await db.agent_chats.bulkPut(nextChats);

      // Locally retained widget chats are intentionally absent from the list until the
      // first submission, so preserve them while pruning list-visible chat history.
      const keepKeys = new Set(chats.map((chat) => this.composite(chat.workspace_id, chat.id)));
      const directWorkspaceIds = await this.directMessageWorkspaceIds();
      const keysToDelete = currentAgentChats
        .filter(
          (row) =>
            !isLocallyRetainedWidgetChat(row) &&
            !directWorkspaceIds.has(String(row.workspace_id)) &&
            !keepKeys.has(this.composite(row.workspace_id, row.id)),
        )
        .map((row) => [row.workspace_id, row.id]);
      if (keysToDelete.length > 0) await db.agent_chats.bulkDelete(keysToDelete);

      await storeFingerprint('agent_chats', hash);
      log('live-journal', `Loaded ${nextChats.length} agent chats and removed ${keysToDelete.length} stale listed chats`);
    });
  }

  async loadMe() {
    await runLoaderWithRetries('loadMe', async () => {
      const { response, data } = await apiRecoveryRead('/me');

      if (!(response.ok && data.ok)) {
        throw new Error(`Failed to fetch me API: ${(Array.isArray(data.errors) ? data.errors : []).join(', ') || 'Unknown error'}`);
      }

      const { user, configs } = data.data;

      if (user) {
        await this.storeUser(user);

        // Keep `/me` fresh without creating a brief "no current user" hole in Dexie.
        const existingIds = await db.users.toCollection().primaryKeys();
        const staleIds = existingIds.filter((id) => String(id) !== String(user.id));
        if (staleIds.length > 0) await db.users.bulkDelete(staleIds);
      } else {
        await db.users.clear();
      }

      const letters = configs?.glyphs?.letters;
      if (Array.isArray(letters) && letters.length) sessionStorage.setItem('output:glyphs:letters', JSON.stringify(letters));

      log('live-journal', `Loaded me API response`);
    });
  }

  async loadWorkspaces({ preserveCurrentWorkspaceRows = true } = {}) {
    await runLoaderWithRetries('loadWorkspaces', async () => {
      const { response, data } = await apiRecoveryRead('/workspaces');

      if (!(response.ok && data.ok)) {
        throw new Error(`Failed to fetch workspaces: ${(Array.isArray(data.errors) ? data.errors : []).join(', ') || 'Unknown error'}`);
      }

      const workspaces = data.data.workspaces || [];

      // The main-app `/workspaces` response is an authoritative user-membership
      // snapshot. A shared-channel response may also contain its shadow-only
      // workspace, so only the non-shadow controller path can reauthorize tombstones.
      await authorizeWorkspaceWidgetBoardsFromMembershipSnapshot(workspaces, { userAuthoritative: !this.isSharedChannel });

      const { changed, hash } = await hasDataChanged('workspaces', workspaces);
      // The fingerprint only covers the API payload, so an unchanged payload does not mean Dexie
      // matches it: a row an earlier `preserveCurrentWorkspaceRows` load held back is still stale.
      // Skip the whole pass only when this call would preserve that row anyway.
      if (!changed && preserveCurrentWorkspaceRows) {
        log('live-journal', `Skipped workspaces bulkPut — fingerprint unchanged (${workspaces.length} workspaces)`);
        return;
      }

      if (changed) await db.workspaces.bulkPut(workspaces);

      const staleWorkspaceIds = await pruneStaleWorkspaces(db, workspaces, {
        preserveWorkspaceId: preserveCurrentWorkspaceRows ? currentWorkspaceId() : null,
      });
      for (const staleWorkspaceId of staleWorkspaceIds) {
        await revokeWorkspaceWidgetBoard(staleWorkspaceId);
      }

      if (changed) await storeFingerprint('workspaces', hash);
      log('live-journal', `Loaded ${workspaces.length} workspaces`);
    });
  }

  // === collection listeners ===

  setupCollectionListeners() {
    // Channels: on added -> subscribe; on removed -> unsubscribe + purge
    // primKey is the compound [workspace_id, id] tuple in db
    function onChannelCreating(primKey, _obj, _transaction) {
      const workspaceId = Array.isArray(primKey) ? primKey[0] : null;
      const channelId = Array.isArray(primKey) ? primKey[1] : primKey;
      if (typeof channelId !== 'string' || channelId.startsWith('skeleton_')) return;
      log('live-journal', 'Channel added, subscribing to channel collections', channelId);
      window.livejournal.subscribeToChannelCollections(channelId, workspaceId);
    }

    function onChannelDeleting(primKey, _obj, _transaction) {
      const workspaceId = Array.isArray(primKey) ? primKey[0] : null;
      const channelId = Array.isArray(primKey) ? primKey[1] : primKey;
      if (typeof channelId !== 'string' || channelId.startsWith('skeleton_')) return;
      log('live-journal', 'Channel removed, unsubscribing from channel collections', channelId);
      window.livejournal.unsubscribeFromChannel(channelId, workspaceId);
    }

    db.channels.hook('creating', onChannelCreating);
    db.channels.hook('deleting', onChannelDeleting);
    this.collectionHooks.onChannelCreating = onChannelCreating;
    this.collectionHooks.onChannelDeleting = onChannelDeleting;

    // Profiles: on changed (updating) -> remove the workspace if current profile deactivated
    const controller = this;
    function onProfileUpdating(_mods, _primKey, _obj, _transaction) {
      this.onsuccess = (updatedObj) => {
        if (!updatedObj || updatedObj.id !== currentProfileId()) return;
        if (updatedObj.role === 'deactivated') {
          log('live-journal', 'Current profile deactivated, purging workspace');
          void controller.killWorkspace(currentWorkspaceId());
        }
      };
    }

    db.profiles.hook('updating', onProfileUpdating);
    this.collectionHooks.onProfileUpdating = onProfileUpdating;

    logDebug('live-journal', 'Subscribed to Dexie CRUD hooks for collections');
  }

  cleanupCollectionListeners() {
    if (this.collectionHooks.onChannelCreating) db.channels.hook('creating').unsubscribe(this.collectionHooks.onChannelCreating);
    if (this.collectionHooks.onChannelDeleting) db.channels.hook('deleting').unsubscribe(this.collectionHooks.onChannelDeleting);
    if (this.collectionHooks.onProfileUpdating) db.profiles.hook('updating').unsubscribe(this.collectionHooks.onProfileUpdating);
  }

  unsubscribeFromChannel(channelId, workspaceId = null) {
    const toDelete = [];
    for (const [dedupeKey, subscription] of this.channelSubscriptions) {
      const [root_type, root_id, collection_name] = this.decompose(dedupeKey);
      if (root_type !== 'workspace') continue;
      if (collection_name !== channelId) continue;
      if (workspaceId != null && String(root_id) !== String(workspaceId)) continue;
      this.releaseSubscription(subscription);
      toDelete.push(dedupeKey);
    }
    for (const k of toDelete) this.channelSubscriptions.delete(k);
    if (toDelete.length) log('live-journal', `Unsubscribed from channel ${channelId}`);
  }

  handleError(error) {
    const errors = [
      `An internal error was encountered in the Indexed Database server`,
      `Connection to Indexed Database server lost`,
      `Database has been closed`,
      `Attempt to iterate a cursor that doesn't exist`,
      `database without an in-progress transaction`, // partial matches most of them
    ];

    // partial matches the error message with any of our fatal error strings
    for (const str of errors) {
      if (error.message.includes(str)) {
        logFatal('live-journal', 'Reloading app due to fatal Dexie error', error);
        window.dispatchEvent(
          new CustomEvent('output:reload', {
            detail: {
              source: 'live-journal',
              reason: 'fatal-dexie-error',
              error: error.message,
            },
          }),
        );
        break;
      }
    }
  }

  // Tracks the cable only. Network events used to feed the same flag, so a cable reconnect that
  // followed a network-online event looked like no change and opened no catchup envelope.
  async handleConnectionChange(event) {
    const connected = event.detail.connected;

    // only update if the connection state changes
    if (this.connected === connected) return;
    this.connected = connected;

    // prepare catchup if we are (re)connecting and the page is actually visible
    if (this.connected && document.visibilityState === 'visible') {
      if (!(await this.prepareVisibleCatchup())) return;
    }

    await this.createNewCatchupEnvelope();
  }

  // === cleanup methods ===

  cleanupSubscriptions() {
    this.cancelSubscriptionReconcile();

    const disconnectAll = (map) => {
      map.forEach((subscription) => this.releaseSubscription(subscription));
      map.clear();
    };

    disconnectAll(this.rootSubscriptions);
    disconnectAll(this.profileSubscriptions);
    disconnectAll(this.channelSubscriptions);

    // cleanup Dexie collection listeners
    this.cleanupCollectionListeners();
  }

  cleanupTracking() {
    if (this.catchup_envelope_tracking_handle) {
      this.catchup_envelope_tracking_handle();
      this.catchup_envelope_tracking_handle = null;
    }

    if (this.visibility_tracking_handle) {
      document.removeEventListener('visibilitychange', this.visibility_tracking_handle);
      this.visibility_tracking_handle = null;
    }

    if (this.process_catchup_envelopes_subscription) {
      this.process_catchup_envelopes_subscription.unsubscribe();
      this.process_catchup_envelopes_subscription = null;
    }

    this.process_catchup_envelopes_in_flight = null;
    if (this.process_catchup_envelopes_interval) {
      clearInterval(this.process_catchup_envelopes_interval);
      this.process_catchup_envelopes_interval = null;
    }
  }

  // === event handling ===

  setupEventHandlers() {
    this.errorHandler = this.handleError.bind(this);
    window.addEventListener('error', this.errorHandler);

    this.connectionChangeHandler = this.handleConnectionChange.bind(this);
    window.addEventListener('output:internal:cable', this.connectionChangeHandler);

    this.batchInsertHandler = this.handleBatchInsert.bind(this);
    window.addEventListener('output:internal:live-journal:batch-insert', this.batchInsertHandler);

    this.workspaceLatestMessagesRefreshHandler = this.handleWorkspaceLatestMessagesRefresh.bind(this);
    window.addEventListener(WORKSPACE_LATEST_MESSAGES_REFRESH_EVENT, this.workspaceLatestMessagesRefreshHandler);

    this.submitMessageHandler = this.handleSubmitMessage.bind(this);
    window.addEventListener('output:internal:live-journal:submit-message', this.submitMessageHandler);

    this.retryAttachmentMessageHandler = this.handleRetryOptimisticMessageAttachments.bind(this);
    window.addEventListener('output:internal:live-journal:retry-optimistic-message-attachments', this.retryAttachmentMessageHandler);

    this.submitReactionHandler = this.handleSubmitReaction.bind(this);
    window.addEventListener('output:internal:live-journal:submit-reaction', this.submitReactionHandler);

    this.removeReactionHandler = this.handleRemoveReaction.bind(this);
    window.addEventListener('output:internal:live-journal:remove-reaction', this.removeReactionHandler);

    this.killYourSelfHandler = this.killYourSelf.bind(this);
    window.addEventListener('output:kill-your-self', this.killYourSelfHandler);

    this.killWorkspaceHandler = this.killWorkspace.bind(this);
    window.addEventListener('output:kill-workspace', this.killWorkspaceHandler);
  }

  cleanupEventHandlers() {
    if (this.errorHandler) {
      window.removeEventListener('error', this.errorHandler);
    }

    if (this.connectionChangeHandler) {
      window.removeEventListener('output:internal:cable', this.connectionChangeHandler);
    }

    if (this.batchInsertHandler) {
      window.removeEventListener('output:internal:live-journal:batch-insert', this.batchInsertHandler);
    }

    if (this.workspaceLatestMessagesRefreshHandler) {
      window.removeEventListener(WORKSPACE_LATEST_MESSAGES_REFRESH_EVENT, this.workspaceLatestMessagesRefreshHandler);
    }

    if (this.submitMessageHandler) {
      window.removeEventListener('output:internal:live-journal:submit-message', this.submitMessageHandler);
    }

    if (this.retryAttachmentMessageHandler) {
      window.removeEventListener('output:internal:live-journal:retry-optimistic-message-attachments', this.retryAttachmentMessageHandler);
    }

    // abort any in-flight reaction submissions
    if (this.reactionAbortControllers) {
      for (const abortController of this.reactionAbortControllers.values()) {
        abortController.abort();
      }
      this.reactionAbortControllers.clear();
    }

    // clear pending reaction submissions
    if (this.pendingReactionSubmissions) {
      this.pendingReactionSubmissions.clear();
    }

    if (this.submitReactionHandler) {
      window.removeEventListener('output:internal:live-journal:submit-reaction', this.submitReactionHandler);
    }

    if (this.removeReactionHandler) {
      window.removeEventListener('output:internal:live-journal:remove-reaction', this.removeReactionHandler);
    }

    if (this.killYourSelfHandler) {
      window.removeEventListener('output:kill-your-self', this.killYourSelfHandler);
    }

    if (this.killWorkspaceHandler) {
      window.removeEventListener('output:kill-workspace', this.killWorkspaceHandler);
    }
  }

  setupAttachmentBackedMessageWatcher() {
    this.mediaAttachmentSubscriptionCleanup = subscribeMediaAttachments(() => {
      this.scheduleAttachmentBackedMessageCheck().catch((error) => {
        logError('live-journal', 'Failed to check attachment-backed optimistic messages', error);
      });
    });
  }

  cleanupAttachmentBackedMessageWatcher() {
    if (this.mediaAttachmentSubscriptionCleanup) {
      this.mediaAttachmentSubscriptionCleanup();
      this.mediaAttachmentSubscriptionCleanup = null;
    }
  }

  async handleBatchInsert(event) {
    const { collection, items } = event.detail;

    logDebug('live-journal', `Batch insert: ${items.length} items into ${collection}`);

    switch (collection) {
      case 'emojis':
        await this.batchInsertEmojis(items);
        break;
      case 'activities':
        await db.activities.bulkPut(items);
        break;
      case 'widgets':
        await this.batchInsertWidgets(items);
        break;
      case 'messages':
        await this.batchInsertMessages(items);
        break;
      case 'channels':
        await this.batchInsertChannels(items);
        break;
      case 'profiles':
        await this.batchInsertProfiles(items);
        break;
      case 'channelReadMarkers':
        await this.batchInsertChannelReadMarkers(items);
        break;
      default:
        logWarn('live-journal', `Unknown collection for batch insert: ${collection}`);
    }
  }

  handleWorkspaceLatestMessagesRefresh() {
    this.workspaceLatestMessagesRefreshPromise ??= this.loadMessageBootstrap({ force: true }).finally(() => {
      this.workspaceLatestMessagesRefreshPromise = null;
    });
    return this.workspaceLatestMessagesRefreshPromise;
  }

  // === batch inserts ===

  async batchInsertEmojis(emojis) {
    await db.emojis.bulkPut(emojis);
    // partial batches are not a full API snapshot; invalidate so loadEmojis() recomputes hash + prunes
    await this.clearCollectionFingerprint('emojis');
  }

  async batchInsertWidgets(widgets) {
    const { changed, hash } = await hasDataChanged('widgets', widgets);
    if (!changed) {
      log('live-journal', `Skipped widgets bulkPut — fingerprint unchanged (${widgets.length} widgets)`);
      return;
    }

    await db.widgets.bulkPut(widgets);
    await storeFingerprint('widgets', hash);
  }

  async batchInsertMessages(messages, { syncWorkspaceProjection = true } = {}) {
    // Check for missing authors in shared channel mode BEFORE the transaction and await reload if needed
    if (this.isSharedChannel && !this.pendingSharedChannelReload) {
      let missingProfiles = false;
      for (const message of messages) {
        if (!message.author_id) continue;
        if (message.author_type === 'profile') {
          const workspaceId = message.workspace_id;
          const existing = workspaceId ? await db.profiles.get([workspaceId, message.author_id]) : null;
          if (!existing) missingProfiles = true;
        }
      }

      // Reload shared channel data if any authors are missing (await to ensure authors load before message is displayed)
      if (missingProfiles) {
        this.pendingSharedChannelReload = true;
        const loadPromises = [];
        if (missingProfiles) loadPromises.push(this.loadProfiles());
        await Promise.all(loadPromises);
        this.pendingSharedChannelReload = false;
      }
    }

    const optimisticUploadIdsToCleanup = new Set();

    // TODO: restore db.transaction('rw', [db.messages, db.mentions], …) once the message path is settled.
    await (async () => {
      logDebug('dexie', 'starting message insert transaction');

      const pksToDelete = [];
      const currentAuthorId = currentProfileId();

      for (const message of messages) {
        const workspaceId = message.workspace_id;
        if (!workspaceId) {
          logError('live-journal', 'Skipping message insert without workspace_id', { message });
          continue;
        }

        // first check if there are any objects before calling primaryKeys (https://github.com/dexie/Dexie.js/issues/1030)
        if ((await messagesByPseudotypeAndSortOrder('skeleton', message.sort_order).count()) !== 0) {
          pksToDelete.push(...(await messagesByPseudotypeAndSortOrder('skeleton', message.sort_order).primaryKeys()));
        }

        // If we're getting a new message that's posted by the current profile we can do some house keeping.
        // First, we record that the optimistic_sort_order for it has past through here.
        // This lets us know to ignore an optimistic thats arriving late or weirdly, later on. We can just avoid inserting it entirely.

        // Either way we attempt to delete it, too.
        if (message.optimistic_sort_order && message.author_id === currentAuthorId) {
          this.processedOptimisticSortOrdersFromRealMessages.add(message.optimistic_sort_order);

          if (this.pendingSubmissions.has(message.optimistic_sort_order)) {
            this.pendingSubmissions.delete(message.optimistic_sort_order);
          }

          // Don't let it grow forever, cut off the set after 100 items
          if (this.processedOptimisticSortOrdersFromRealMessages.size > 100) {
            const firstKey = this.processedOptimisticSortOrdersFromRealMessages.keys().next().value;
            this.processedOptimisticSortOrdersFromRealMessages.delete(firstKey);
          }

          // Generally speaking, when we do get a real message back, we want to delete the optimistic that we had inserted.
          logDebug('live-journal', `Deleting optimistic message ${message.optimistic_sort_order}`);
          if ((await messagesByPseudotypeAndSortOrder('optimistic', message.optimistic_sort_order).count()) !== 0) {
            const optimisticMessage = await messagesByPseudotypeAndSortOrder('optimistic', message.optimistic_sort_order).first();
            if (optimisticMessage) {
              getUploadIds(optimisticMessage).forEach((uploadId) => {
                optimisticUploadIdsToCleanup.add(uploadId);
              });
            }
            pksToDelete.push(...(await messagesByPseudotypeAndSortOrder('optimistic', message.optimistic_sort_order).primaryKeys()));
          }
        }

        // Extract mentions from the data being written to the messages table
        const { mentions, reactions, ...messageData } = message;

        // We need to convert the reactions array to an object
        messageData.reactions = {};
        if (reactions && reactions.length) {
          reactions.forEach((r) => {
            messageData.reactions[r.id] = r;
          });
        }

        // Upsert the message, bumping the fresh_since timestamp
        await this.messagePut(messageData);

        // Clear any existing mentions and upsert the new ones
        await db.mentions.where('[workspace_id+mentionable_id]').equals([message.workspace_id, message.id]).delete();
        if (mentions && mentions.length) await db.mentions.bulkPut(mentions);
      }

      if (pksToDelete.length) {
        logDebug('live-journal', `Bulk deleting ${pksToDelete.length} pseudotypes by their primary keys`, pksToDelete);
        await db.messages.bulkDelete(pksToDelete);
      }
    })();

    if (optimisticUploadIdsToCleanup.size > 0) {
      await removeMediaAttachmentsByUploadIds(Array.from(optimisticUploadIdsToCleanup));
    }

    if (syncWorkspaceProjection) await syncWorkspaceLatestMessageChanges({ upserts: messages });

    logDebug('dexie', 'finished message insert transaction');
  }

  skeletonDataForSortOrder(channelId, sortOrder, workspaceId) {
    if (!workspaceId) throw new Error('workspaceId is required for message skeletons');

    return {
      id: `skeleton_${sortOrder}`,
      workspace_id: workspaceId,
      pseudotype: 'skeleton',
      channel_id: channelId,
      sort_order: sortOrder,
      timestamp: Math.floor(decodeTime(sortOrder) / 1000), // millis to seconds
      skeleton_seed: Math.random(), // stable random number for skeleton size variation
    };
  }

  async bulkApplyReadMarkerMessageSkeletons(createRows, removeRows) {
    const removeTuples = (removeRows || []).filter((row) => row?.sort_order != null).map((row) => ['skeleton', row.sort_order]);
    if (removeTuples.length) {
      const existing = await db.messages.where('[pseudotype+sort_order]').anyOf(removeTuples).count();
      if (existing > 0) {
        const pksToDelete = await db.messages.where('[pseudotype+sort_order]').anyOf(removeTuples).primaryKeys();
        if (pksToDelete.length) {
          await db.messages.bulkDelete(pksToDelete);
          logDebug('skeletons', `Bulk removed ${pksToDelete.length} message skeleton(s)`);
        }
      }
    }

    const newSkeletons = [];
    for (const row of createRows || []) {
      const workspaceId = row.workspace_id;
      const channelId = row.channel_id;
      const sortOrder = row.sort_order;
      if (!workspaceId || !channelId || sortOrder == null) continue;
      const existing = await messagesByChannelAndSortOrder(channelId, sortOrder).count();
      if (existing > 0) continue;
      newSkeletons.push(this.skeletonDataForSortOrder(channelId, sortOrder, workspaceId));
    }

    if (newSkeletons.length) {
      await this.messageBulkPut(newSkeletons, false);
      log('skeletons', `Bulk inserted ${newSkeletons.length} message skeleton(s) from read-marker snapshot`);
    }
  }

  mentionSkeletonDataForRow(row) {
    return {
      id: `skeleton_${row.sort_order}`,
      workspace_id: row.workspace_id,
      pseudotype: 'skeleton',
      source_id: row.channel_id,
      source_type: 'channel',
      recipient_id: row.profile_id,
      sort_order: row.sort_order,
      timestamp: Math.floor(decodeTime(row.sort_order) / 1000), // millis to seconds
    };
  }

  // Unread mentions can age out of the capped /mentions and bootstrap payloads while
  // still being unread; the read-marker snapshot ships skeleton rows for them so the
  // unread counts survive. Mirrors bulkApplyReadMarkerMessageSkeletons.
  async bulkApplyReadMarkerMentionSkeletons(createRows, removeRows) {
    const removeTuples = (removeRows || [])
      .filter((row) => row?.workspace_id != null && row?.sort_order != null)
      .map((row) => [row.workspace_id, row.sort_order]);
    if (removeTuples.length) {
      const keys = await db.mentions.where('[workspace_id+sort_order]').anyOf(removeTuples).primaryKeys();
      if (keys.length) {
        await db.mentions.bulkDelete(keys);
        logDebug('skeletons', `Bulk removed ${keys.length} mention row(s) from read-marker snapshot`);
      }
    }

    const newSkeletons = [];
    for (const row of createRows || []) {
      if (row?.workspace_id == null || row?.channel_id == null || row?.profile_id == null || row?.sort_order == null) continue;
      const existing = await db.mentions.where('[workspace_id+sort_order]').equals([row.workspace_id, row.sort_order]).count();
      if (existing > 0) continue;
      newSkeletons.push(this.mentionSkeletonDataForRow(row));
    }

    if (newSkeletons.length) {
      await db.mentions.bulkPut(newSkeletons);
      log('skeletons', `Bulk inserted ${newSkeletons.length} mention skeleton(s) from read-marker snapshot`);
    }
  }

  // Deletes skeleton twins for real mention rows that just landed, so a skeleton and
  // its resolved mention never double-count.
  async removeMentionSkeletonTwins(mentions) {
    const keys = (mentions || [])
      .filter((mention) => mention?.workspace_id != null && mention?.sort_order != null && !String(mention.id || '').startsWith('skeleton_'))
      .map((mention) => [mention.workspace_id, `skeleton_${mention.sort_order}`]);
    if (keys.length) await db.mentions.bulkDelete(keys);
  }

  async batchInsertChannels(channels) {
    // Remove any channel skeletons before put (compound key: [workspace_id, id])
    const skeletonKeys = channels.filter((c) => c.workspace_id != null).map((c) => [c.workspace_id, `skeleton_${c.id}`]);
    if (skeletonKeys.length) await db.channels.bulkDelete(skeletonKeys);
    await db.channels.bulkPut(channels);
  }

  async batchInsertProfiles(profiles) {
    await db.profiles.bulkPut(profiles);
  }

  async batchInsertChannelReadMarkers(readMarkers) {
    if (readMarkers.length === 0) return;
    // bulkPut upserts by PK [workspace_id+id]. Do NOT add a preceding
    // .where('[workspace_id+channel_id]').delete() — that would erase markers
    // belonging to the user's other profiles in the same workspace.
    await bulkPutMergedChannelReadMarkers(readMarkers);
  }

  resolveAttachmentBackedMessageAttachments(message = {}, attachmentSnapshot = getMediaAttachmentSnapshot()) {
    const uploadIds = getUploadIds(message);
    if (uploadIds.length === 0) return Array.isArray(message.attachments) ? message.attachments : [];

    // Upload ids are the source of truth; message snapshot is just a fallback.
    // The live Dexie rows carry progress, retries, errors, and resolved attachment ids.
    return uploadIds.map((uploadId) => attachmentSnapshot.get(uploadId)).filter(Boolean);
  }

  inspectAttachmentBackedMessageState(message = {}, attachmentSnapshot = getMediaAttachmentSnapshot()) {
    const uploadIds = getUploadIds(message);
    const attachments = this.resolveAttachmentBackedMessageAttachments(message, attachmentSnapshot);

    if (uploadIds.length === 0) return { status: 'ready', attachments };

    // Still waiting for the attachment store to catch up.
    if (attachments.length !== uploadIds.length) return { status: 'pending', attachments };

    const failedAttachment = attachments.find((a) => a?.upload?.state === MediaAttachmentStates.error);
    if (failedAttachment) {
      return { status: 'failed', attachments, failureReason: failedAttachment?.error || 'attachment upload failed' };
    }

    const ready = attachments.every((a) => a?.upload?.state === MediaAttachmentStates.completed && Boolean(a?.attachment_id));
    return { status: ready ? 'ready' : 'pending', attachments };
  }

  async cleanupOptimisticMessageUploads(message = {}) {
    const uploadIds = getUploadIds(message);
    if (!uploadIds.length) return;

    await removeMediaAttachmentsByUploadIds(uploadIds);
  }

  async updateOptimisticMessage(optimistic_sort_order, updates = {}) {
    if (!optimistic_sort_order) return null;
    const optimisticMessageId = `optimistic_${optimistic_sort_order}`;
    const existingMessage = await messageById(optimisticMessageId);
    if (!existingMessage) return null;

    const updatedCount = await db.messages.update(optimisticMessageId, updates);
    if (updatedCount === 0) return null;

    const updatedMessage = {
      ...existingMessage,
      ...updates,
    };

    this.dispatchLiveMessageEvent('update', updatedMessage);
    return updatedMessage;
  }

  async updateOptimisticMessageStatus(optimistic_sort_order, status, { retryAttempt, retryMaxAttempts, nextRetryAt, lastError } = {}) {
    const updates = { status };
    if (retryAttempt !== undefined) updates.retry_attempt = Math.max(0, Math.floor(retryAttempt)) || 0;
    if (retryMaxAttempts !== undefined) updates.retry_max_attempts = Math.max(1, Math.floor(retryMaxAttempts)) || 1;
    if (nextRetryAt !== undefined) updates.next_retry_at = Number.isFinite(nextRetryAt) ? nextRetryAt : null;
    if (lastError !== undefined) updates.last_error = lastError || null;
    return this.updateOptimisticMessage(optimistic_sort_order, updates);
  }

  async scheduleAttachmentBackedMessageCheck() {
    if (this.attachmentBackedMessageCheckPromise) {
      // Coalesce noisy progress updates — one rerun flag is enough.
      this.attachmentBackedMessageCheckRequested = true;
      return this.attachmentBackedMessageCheckPromise;
    }

    this.attachmentBackedMessageCheckPromise = (async () => {
      do {
        this.attachmentBackedMessageCheckRequested = false;
        await this.checkAttachmentBackedMessages();
      } while (this.attachmentBackedMessageCheckRequested);
    })().finally(() => {
      this.attachmentBackedMessageCheckPromise = null;
    });

    return this.attachmentBackedMessageCheckPromise;
  }

  async checkAttachmentBackedMessages() {
    const optimisticRows = await allOptimisticMessages()
      .filter((msg) => {
        const s = msg.status || SENDING_STATUS;
        return s === WAITING_FOR_ATTACHMENTS_STATUS;
      })
      .toArray();

    if (optimisticRows.length === 0) return;

    const attachmentSnapshot = getMediaAttachmentSnapshot();
    for (const message of optimisticRows) {
      const optimisticSortOrder = resolveOptimisticSortOrder(message);

      if (!optimisticSortOrder || message?.status !== WAITING_FOR_ATTACHMENTS_STATUS) continue;

      // Each optimistic row only cares about its own uploads — no cross-message ordering.
      const attachmentState = this.inspectAttachmentBackedMessageState(message, attachmentSnapshot);

      if (attachmentState.status === 'failed') {
        await this.updateOptimisticMessage(optimisticSortOrder, {
          status: FAILED_ATTACHMENT_STATUS,
          attachments: attachmentState.attachments,
          last_error: attachmentState.failureReason || 'attachment upload failed',
        });
        continue;
      }

      if (attachmentState.status !== 'ready') continue;

      await this.beginOptimisticMessageSubmission(
        {
          ...message,
          attachments: attachmentState.attachments,
        },
        { startAttempt: 0 },
      );
    }
  }

  async beginOptimisticMessageSubmission(
    message,
    { startAttempt = 0, maxRetries = OPTIMISTIC_MESSAGE_MAX_RETRIES, initialDelay = OPTIMISTIC_MESSAGE_INITIAL_RETRY_DELAY_MS } = {},
  ) {
    const optimistic_sort_order = resolveOptimisticSortOrder(message);
    if (!optimistic_sort_order) return;

    if (this.pendingSubmissions.has(optimistic_sort_order)) {
      logWarn('live-journal', 'Duplicate submission detected, skipping', { optimistic_sort_order });
      return;
    }

    this.pendingSubmissions.add(optimistic_sort_order);

    try {
      await this.updateOptimisticMessage(optimistic_sort_order, {
        status: SENDING_STATUS,
        attachments: Array.isArray(message.attachments) ? message.attachments : [],
        retry_attempt: Math.max(0, Math.floor(startAttempt)),
        retry_max_attempts: Math.max(1, Math.floor(maxRetries)),
        next_retry_at: null,
        last_error: null,
      });

      await this.submitMessageWithRetry(
        typeof message.text === 'string' ? message.text : '',
        Array.isArray(message.attachments) ? message.attachments : [],
        message.channel_id,
        optimistic_sort_order,
        message.reply_to_message_id || null,
        {
          startAttempt,
          maxRetries,
          initialDelay,
        },
      );
    } catch (error) {
      logError('live-journal', 'Message submission failed after retries:', error);
      await this.updateOptimisticMessageStatus(optimistic_sort_order, FAILED_STATUS, {
        retryAttempt: maxRetries,
        retryMaxAttempts: maxRetries,
        nextRetryAt: null,
        lastError: error?.message || 'message submission failed',
      });
    } finally {
      this.pendingSubmissions.delete(optimistic_sort_order);
    }
  }

  async resumeOptimisticMessageRetries() {
    try {
      const optimisticRows = await allOptimisticMessages().toArray();

      if (optimisticRows.length === 0) {
        logDebug('live-journal', 'No optimistic messages found for rehydration');
        return;
      }

      let resumableCount = 0;
      let waitingForAttachmentsCount = 0;

      for (const message of optimisticRows) {
        const optimistic_sort_order = resolveOptimisticSortOrder(message);
        if (!optimistic_sort_order) continue;
        const workspace_id = message.workspace_id;
        if (!workspace_id) continue;

        const { retryAttempt, retryMaxAttempts } = this.normalizeOptimisticRetryMetadata(message);

        // Rehydrate persisted optimistic rows into any active timeline before retry decisions.
        this.dispatchLiveMessageEvent('create', message);

        if (message.status === WAITING_FOR_ATTACHMENTS_STATUS) {
          waitingForAttachmentsCount += 1;
          continue;
        }

        if (message.status !== SENDING_STATUS) continue;

        if (retryAttempt >= retryMaxAttempts) {
          await this.updateOptimisticMessageStatus(optimistic_sort_order, FAILED_STATUS, {
            retryAttempt,
            retryMaxAttempts,
            nextRetryAt: null,
            lastError: message.last_error || 'automatic retry budget exhausted',
          });
          continue;
        }

        resumableCount += 1;
        const { text, channel_id, attachments, reply_to_message_id } = message;

        // dispatch the submit event to resume retrying
        // this will reuse the existing retry logic
        window.dispatchEvent(
          new CustomEvent('output:internal:live-journal:submit-message', {
            bubbles: true,
            detail: {
              messageData: {
                text,
                channel_id,
                workspace_id,
                attachments: attachments || [],
                optimistic_sort_order,
                reply_to_message_id: reply_to_message_id || null,
                retry_mode: 'resume',
              },
            },
          }),
        );
      }

      if (waitingForAttachmentsCount > 0) {
        await this.scheduleAttachmentBackedMessageCheck();
      }

      logDebug('live-journal', `Rehydrated ${optimisticRows.length} optimistic messages; resuming ${resumableCount} sends`);
    } catch (error) {
      logError('live-journal', 'Failed to resume optimistic message retries', error);
    }
  }

  // === message submission handling ===

  async handleSubmitMessage(event) {
    const messageData = event?.detail?.messageData || {};
    const channel_id = messageData.channel_id;
    const workspace_id = messageData.workspace_id;
    const text = typeof messageData.text === 'string' ? messageData.text : '';
    const attachments = Array.isArray(messageData.attachments) ? messageData.attachments : [];
    const upload_ids = Array.isArray(messageData.upload_ids) ? messageData.upload_ids.filter(Boolean) : [];
    const { reply_to_message_id, retry_mode } = messageData;
    let { optimistic_sort_order } = messageData;
    const hasAttachmentUploads = upload_ids.length > 0;

    if (!channel_id) {
      logWarn('live-journal', 'Skipping message submission with missing channel_id', { messageData });
      return;
    }
    if (!workspace_id) {
      logError('live-journal', 'Skipping message submission with missing workspace_id', { messageData });
      return;
    }

    // Use provided optimistic_sort_order (for retries) or generate a new one
    const isRetry = !!optimistic_sort_order;
    const isResumeRetry = retry_mode === 'resume';
    optimistic_sort_order = optimistic_sort_order || ulid();

    const optimisticMessageId = `optimistic_${optimistic_sort_order}`;
    let existingOptimisticMessage = await messageById(optimisticMessageId);
    const { retryAttempt: persistedRetryAttempt, retryMaxAttempts: persistedRetryMaxAttempts } =
      this.normalizeOptimisticRetryMetadata(existingOptimisticMessage);

    const maxRetries = persistedRetryMaxAttempts;
    const startAttempt = isRetry && isResumeRetry ? persistedRetryAttempt : 0;

    logDebug('live-journal', 'Handling message submission', {
      messageData,
      optimistic_sort_order,
      attachments,
      upload_ids,
      isRetry,
      retry_mode: retry_mode || null,
      startAttempt,
      maxRetries,
    });

    if (!isRetry || !existingOptimisticMessage) {
      if (!this.processedOptimisticSortOrdersFromRealMessages.has(optimistic_sort_order)) {
        // Create the optimistic message now even if uploads aren't done yet.
        // It becomes the long-lived owner of the upload ids, freeing the composer.
        await createOptimisticMessage({
          text,
          attachments,
          channelId: channel_id,
          workspaceId: workspace_id,
          optimisticSortOrder: optimistic_sort_order,
          replyToMessageId: reply_to_message_id,
          status: hasAttachmentUploads ? WAITING_FOR_ATTACHMENTS_STATUS : SENDING_STATUS,
          uploadIds: upload_ids,
        });

        if (hasAttachmentUploads) {
          // Move upload rows under explicit optimistic-message ownership.
          // Progress/errors now render under the message bubble, not the composer.
          await setMediaAttachmentsOwner({
            uploadIds: upload_ids,
            ownerType: MediaAttachmentOwnerTypes.optimisticMessage,
            ownerId: optimisticMessageOwnerId(channel_id, optimistic_sort_order),
            sessionKey: null,
          });
        }
      }

      existingOptimisticMessage = await messageById(optimisticMessageId);
    }

    const existingStatus = existingOptimisticMessage?.status || SENDING_STATUS;
    if (existingStatus === WAITING_FOR_ATTACHMENTS_STATUS || existingStatus === FAILED_ATTACHMENT_STATUS) {
      // Uploads not done yet — nudge the checker and wait.
      await this.scheduleAttachmentBackedMessageCheck();
      return;
    }

    await this.beginOptimisticMessageSubmission(
      {
        ...existingOptimisticMessage,
        text,
        channel_id,
        workspace_id,
        attachments: attachments.length ? attachments : this.resolveAttachmentBackedMessageAttachments(existingOptimisticMessage),
        reply_to_message_id: reply_to_message_id || existingOptimisticMessage.reply_to_message_id || null,
      },
      {
        startAttempt,
        maxRetries,
        initialDelay: OPTIMISTIC_MESSAGE_INITIAL_RETRY_DELAY_MS,
      },
    );
  }

  async handleRetryOptimisticMessageAttachments(event) {
    const optimisticSortOrder = event?.detail?.optimisticSortOrder || null;
    if (!optimisticSortOrder) return;

    const optimisticMessage = await messageById(`optimistic_${optimisticSortOrder}`);
    if (!optimisticMessage) return;

    const uploadIds = getUploadIds(optimisticMessage);
    if (!uploadIds.length) {
      await this.updateOptimisticMessage(optimisticSortOrder, {
        status: WAITING_FOR_ATTACHMENTS_STATUS,
        last_error: null,
      });
      await this.scheduleAttachmentBackedMessageCheck();
      return;
    }

    try {
      // Retries happen in the attachment store. Once uploads recover, this message
      // re-enters the normal send path.
      await retryMediaAttachmentsByUploadIds(uploadIds);
      await this.updateOptimisticMessage(optimisticSortOrder, {
        status: WAITING_FOR_ATTACHMENTS_STATUS,
        last_error: null,
        attachments: this.resolveAttachmentBackedMessageAttachments(optimisticMessage),
      });
      await this.scheduleAttachmentBackedMessageCheck();
    } catch (error) {
      logError('live-journal', 'Failed to retry optimistic message attachments', { optimisticSortOrder, error });
      await this.updateOptimisticMessage(optimisticSortOrder, {
        status: FAILED_ATTACHMENT_STATUS,
        last_error: error?.message || 'attachment retry failed',
      });
    }
  }

  async submitMessageWithRetry(
    text,
    attachments,
    channel_id,
    optimistic_sort_order,
    reply_to_message_id = null,
    { startAttempt = 0, maxRetries = OPTIMISTIC_MESSAGE_MAX_RETRIES, initialDelay = OPTIMISTIC_MESSAGE_INITIAL_RETRY_DELAY_MS } = {},
  ) {
    let attempt = Math.max(0, Math.floor(startAttempt));

    while (attempt < maxRetries) {
      if (!this.pendingSubmissions.has(optimistic_sort_order)) {
        logDebug('live-journal', 'Optimistic submission was cleared before retry attempt', { optimistic_sort_order });
        break;
      }

      const optimisticMessage = await messageById(`optimistic_${optimistic_sort_order}`);
      if (!optimisticMessage) {
        logDebug('live-journal', 'Optimistic row missing before retry attempt, aborting retries', { optimistic_sort_order });
        break;
      }

      const retryAttempt = attempt;
      const isRetryAttempt = retryAttempt > 0;

      if (isRetryAttempt) {
        const delay = this.computeOptimisticRetryDelay({
          retryAttempt,
          initialDelayMs: initialDelay,
        });
        const nextRetryAt = Date.now() + delay;

        await this.updateOptimisticMessageStatus(optimistic_sort_order, SENDING_STATUS, {
          retryAttempt,
          retryMaxAttempts: maxRetries,
          nextRetryAt,
        });

        await new Promise((resolve) => setTimeout(resolve, delay));
      }

      try {
        logDebug('live-journal', `Submitting message attempt ${attempt + 1}/${maxRetries}`, {
          optimistic_sort_order,
          retryAttempt,
        });

        await this.updateOptimisticMessageStatus(optimistic_sort_order, SENDING_STATUS, {
          retryAttempt,
          retryMaxAttempts: maxRetries,
          nextRetryAt: null,
          lastError: null,
        });

        // Just a gaurd against wayward arrays being passed, rather than join'd strings.
        // Might be nice to make the server actually accept an array?
        const normalizedAttachments = Array.isArray(attachments) ? attachments : [];
        const safeAttachments = normalizedAttachments
          .map((attachment) => {
            if (!attachment) return null;
            if (typeof attachment === 'string') return attachment;
            return attachment.attachment_id || null;
          })
          .filter(Boolean)
          .join(',');

        // Submit the message
        const messageBody = { text, optimistic_sort_order, attachments: safeAttachments };
        if (reply_to_message_id) messageBody.reply_to_message_id = reply_to_message_id;

        const response = await apiPost(`/channels/${channel_id}/messages`, { message: messageBody });

        const data = await response.json();
        const shouldStopRetrying = await this.handleMessageSubmissionResponse({
          response,
          data,
          channel_id,
          optimistic_sort_order,
          retryAttempt,
          retryMaxAttempts: maxRetries,
        });
        if (shouldStopRetrying) {
          break;
        }
      } catch (error) {
        attempt++;

        // Final failure
        if (attempt >= maxRetries) throw error;

        logWarn('live-journal', `Submit attempt ${attempt} failed, retrying...`, {
          error: error.message,
          optimistic_sort_order,
        });

        await this.updateOptimisticMessageStatus(optimistic_sort_order, SENDING_STATUS, {
          retryAttempt: attempt,
          retryMaxAttempts: maxRetries,
          nextRetryAt: null,
          lastError: error.message,
        });
      }
    }
  }

  // Server accepted — swap in the real message and clean up the optimistic row.
  async finishSuccessfulMessageSubmission(data, optimistic_sort_order, retryAttempt, retryMaxAttempts) {
    logDebug('live-journal', 'Message submitted successfully', { optimistic_sort_order });

    if (data.data?.message) {
      // Feed the real message from the POST response directly into the live-message path
      // so the optimistic row is reconciled immediately, without waiting for ActionCable.
      await this.handleLiveMessageEvent({ action: 'create', data: { message: data.data.message } });
    } else {
      await this.updateOptimisticMessageStatus(optimistic_sort_order, COMPLETED_STATUS, {
        retryAttempt,
        retryMaxAttempts,
        nextRetryAt: null,
        lastError: null,
      });
      const optimisticMessage = await messageById(`optimistic_${optimistic_sort_order}`);
      if (optimisticMessage) {
        await this.cleanupOptimisticMessageUploads(optimisticMessage);
      }
    }

    // Delete the optimistic row so it doesn't get rehydrated on page load.
    // The real message now owns the canonical attachment ids.
    await messagesByPseudotypeAndSortOrder('optimistic', optimistic_sort_order).delete();
  }

  hasApiValidationPayload(data = {}) {
    return !!(data?.validations && typeof data.validations === 'object' && Object.keys(data.validations).length > 0);
  }

  isTerminalApiClientError(response, data = {}) {
    const status = Number(response?.status);
    if (this.hasApiValidationPayload(data)) return true;
    return Number.isFinite(status) && status >= 400 && status < 500;
  }

  // If we get a terminal client error from the API, clean up the optimistic row and let the UI drop it.
  async rejectOptimisticMessageSubmission(channel_id, optimistic_sort_order, errors = [], validations = null) {
    // This one isn't getting retried, so clear the optimistic row instead.
    logWarn('live-journal', 'Received a terminal API error, aborting retry and remove the optimistic message', errors, validations);
    const optimisticMessage = await messageById(`optimistic_${optimistic_sort_order}`);
    if (optimisticMessage) {
      await this.cleanupOptimisticMessageUploads(optimisticMessage);
    }
    await messagesByPseudotypeAndSortOrder('optimistic', optimistic_sort_order).delete();
    this.dispatchLiveMessageEvent('destroy', {
      id: `optimistic_${optimistic_sort_order}`,
      pseudotype: 'optimistic',
      channel_id,
      sort_order: optimistic_sort_order,
      optimistic_sort_order,
    });
  }

  // Turn the POST result into either "we're done" or "throw and let the retry path handle it."
  async handleMessageSubmissionResponse({ response, data, channel_id, optimistic_sort_order, retryAttempt, retryMaxAttempts }) {
    if (response.ok && data.ok) {
      await this.finishSuccessfulMessageSubmission(data, optimistic_sort_order, retryAttempt, retryMaxAttempts);
      return true;
    }

    const errors = Object.keys(data.errors);
    if (this.isTerminalApiClientError(response, data)) {
      await this.rejectOptimisticMessageSubmission(channel_id, optimistic_sort_order, errors, data?.validations);
      return true;
    }

    if (errors.length > 0) {
      logWarn('live-journal', 'Received a server error, retrying', errors, data.validations);
      throw new Error(`Server error: ${errors.join(', ') || 'Invalid message'}`);
    }

    // Unknown error format - retry it
    throw new Error(`Unknown error: ${response.status} ${response.statusText}`);
  }

  async messagePut(message, setFreshSince = true) {
    if (setFreshSince) message.fresh_since = Date.now();
    return db.messages.put(message);
  }

  async messageBulkPut(messages, setFreshSince = true) {
    const now = Date.now();
    if (setFreshSince) messages.forEach((message) => (message.fresh_since = now));
    return db.messages.bulkPut(messages);
  }

  async messageBulkMerge(messages, setFreshSince = true) {
    const existing = await db.messages.bulkGet(messages.map((message) => message.id));
    const merged = messages.map((message, index) => mergeCachedRecord(existing[index], message, ['data']));
    return this.messageBulkPut(merged, setFreshSince);
  }

  // === reaction submission handling ===

  async createOptimisticReaction(
    channelId,
    messageId,
    emojiId,
    workspaceId,
    optimisticSortOrder,
    posX = null,
    posY = null,
    scale = null,
    angle = null,
  ) {
    const profileId = currentProfileId();
    if (!profileId) {
      logWarn('live-journal', 'Cannot create optimistic reaction: no profile ID');
      return;
    }

    if (!workspaceId) {
      logError('live-journal', 'Cannot create optimistic reaction: no workspace_id', {
        channelId,
        messageId,
        emojiId,
        optimisticSortOrder,
      });
      return;
    }

    if (!emojiId) {
      logWarn('live-journal', 'Cannot create optimistic reaction: no emoji ID');
      return;
    }

    logDebug('live-journal', 'Creating optimistic reaction', { channelId, messageId, emojiId, optimisticSortOrder });

    // get emoji snapshot if available (for immediate rendering)
    let emojiSnapshot = null;
    try {
      emojiSnapshot = (await db.emojis.get(['system', emojiId])) ?? (await db.emojis.get([workspaceId, emojiId]));
    } catch {
      // emoji might not be in db yet, that's ok
    }

    const optimisticId = `optimistic_${optimisticSortOrder}`;
    const optimisticReaction = {
      id: optimisticId,
      workspace_id: workspaceId,
      pseudotype: 'optimistic',
      message_id: messageId,
      channel_id: channelId,
      emoji_id: emojiId,
      emoji: emojiSnapshot, // attach emoji snapshot for immediate rendering if available
      profile_id: profileId,
      pos_x: posX,
      pos_y: posY,
      scale: scale,
      angle: angle,
      sort_order: optimisticSortOrder,
      optimistic_sort_order: optimisticSortOrder,
    };

    // update dexie immediately for optimistic ui
    await db.messages
      .where('id')
      .equals(messageId)
      .modify((message) => {
        if (!message.reactions) message.reactions = {};
        message.reactions[optimisticId] = optimisticReaction;
      });

    // dispatch event to update ui immediately
    window.dispatchEvent(
      new CustomEvent(LIVE_REACTION_EVENT, {
        detail: { action: 'create', data: optimisticReaction },
      }),
    );
  }

  async handleSubmitReaction(event) {
    const { reactionData } = event.detail;
    const workspace_id = reactionData.workspace_id;
    if (!workspace_id) {
      logError('live-journal', 'Skipping reaction submission with missing workspace_id', { reactionData });
      return;
    }
    let { channel_id, message_id, emoji_id, optimistic_sort_order, pos_x, pos_y, scale, angle } = reactionData;

    // Use provided optimistic_sort_order (for retries) or generate a new one
    const isRetry = !!optimistic_sort_order;
    optimistic_sort_order = optimistic_sort_order || ulid();

    logDebug('live-journal', 'Handling reaction submission', { reactionData, optimistic_sort_order, isRetry });

    // Track pending submissions to avoid duplicates
    if (this.pendingReactionSubmissions.has(optimistic_sort_order)) {
      logWarn('live-journal', 'Duplicate reaction submission detected, skipping');
      return;
    }

    this.pendingReactionSubmissions.add(optimistic_sort_order);

    // create abort controller for this submission
    const abortController = new AbortController();
    this.reactionAbortControllers.set(optimistic_sort_order, abortController);

    try {
      if (!isRetry) {
        // For new reactions, create a new optimistic reaction
        await this.createOptimisticReaction(
          channel_id,
          message_id,
          emoji_id,
          workspace_id,
          optimistic_sort_order,
          pos_x,
          pos_y,
          scale,
          angle,
        );
      }

      await this.submitReactionWithRetry(
        channel_id,
        message_id,
        emoji_id,
        workspace_id,
        optimistic_sort_order,
        pos_x,
        pos_y,
        scale,
        angle,
        abortController.signal,
      );
    } catch (error) {
      // ignore abort errors - they're expected when user cancels
      if (error.name === 'AbortError') {
        logDebug('live-journal', 'Reaction submission aborted', { optimistic_sort_order });
        return;
      }
      logError('live-journal', 'Reaction submission failed after retries:', error);
      await this.removeOptimisticReaction(message_id, optimistic_sort_order);
    } finally {
      this.pendingReactionSubmissions.delete(optimistic_sort_order);
      this.reactionAbortControllers.delete(optimistic_sort_order);
    }
  }

  async submitReactionWithRetry(
    channelId,
    messageId,
    emojiId,
    workspaceId,
    optimisticSortOrder,
    posX = null,
    posY = null,
    scale = null,
    angle = null,
    signal = null,
    maxRetries = 3,
    initialDelay = 1000,
  ) {
    let attempt = 0;
    let delay = initialDelay;

    while (attempt < maxRetries) {
      try {
        // check if aborted before attempting
        if (signal?.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }

        logDebug('live-journal', `Submitting reaction attempt ${attempt + 1}/${maxRetries}`, {
          optimisticSortOrder,
          delay: attempt > 0 ? delay : 0,
        });

        // Add delay for retry attempts (check abort during delay)
        if (attempt > 0) {
          await new Promise((resolve) => {
            const timeoutId = setTimeout(resolve, delay);
            signal?.addEventListener('abort', () => {
              clearTimeout(timeoutId);
              resolve();
            });
          });
          // check again after delay
          if (signal?.aborted) {
            throw new DOMException('Aborted', 'AbortError');
          }
        }

        // Submit the reaction
        const reactionBody = { emoji_id: emojiId };
        if (optimisticSortOrder !== null) reactionBody.optimistic_sort_order = optimisticSortOrder;
        if (posX !== null) reactionBody.pos_x = posX;
        if (posY !== null) reactionBody.pos_y = posY;
        if (scale !== null) reactionBody.scale = scale;
        if (angle !== null) reactionBody.angle = angle;

        const response = await apiPost(
          `/channels/${channelId}/messages/${messageId}/reactions`,
          { message_reaction: reactionBody },
          { signal },
        );

        const data = await response.json();
        if (response.ok && data.ok) {
          logDebug('live-journal', 'Reaction submitted successfully', { optimisticSortOrder });
          // the real reaction will be added by live journal events, optimistic will be replaced
          return;
        }
        const errors = Object.keys(data.errors);
        if (this.isTerminalApiClientError(response, data)) {
          logWarn(
            'live-journal',
            'Received a terminal API error, aborting retry and remove the optimistic reaction',
            errors,
            data?.validations,
          );
          await this.removeOptimisticReaction(messageId, optimisticSortOrder);
          return;
        }
        if (errors.length > 0) {
          logWarn('live-journal', 'Received a server error, retrying', errors, data?.validations);
          throw new Error(`Server error: ${errors.join(', ') || 'Invalid reaction'}`);
        }
        // Unknown error format - retry it
        throw new Error(`Unknown error: ${response.status} ${response.statusText}`);
      } catch (error) {
        // if aborted, propagate immediately
        if (error.name === 'AbortError' || signal?.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }

        attempt++;

        // Final failure
        if (attempt >= maxRetries) throw error;

        logWarn('live-journal', `Submit reaction attempt ${attempt} failed, retrying...`, { error: error.message, delay });

        // Exponential backoff with jitter
        delay = Math.min(delay * 2 + Math.random() * 1000, 30000); // Cap at 30 seconds
      }
    }
  }

  async handleRemoveReaction(event) {
    const { channel_id, workspace_id, message_id, reaction_id } = event.detail;
    if (!workspace_id) {
      logError('live-journal', 'Skipping reaction removal with missing workspace_id', { channel_id, message_id, reaction_id });
      return;
    }

    // check if this is an optimistic reaction (id starts with "optimistic_")
    if (reaction_id?.startsWith('optimistic_')) {
      // extract optimistic_sort_order from the id (format: "optimistic_<sort_order>")
      const optimisticSortOrder = reaction_id.replace('optimistic_', '');

      logDebug('live-journal', 'Removing optimistic reaction', { message_id, optimisticSortOrder });

      // mark as cancelled to prevent it from being added when server responds
      this.cancelledReactionSubmissions.add(optimisticSortOrder);

      // abort in-flight request if it exists
      const abortController = this.reactionAbortControllers.get(optimisticSortOrder);
      if (abortController) {
        abortController.abort();
        this.reactionAbortControllers.delete(optimisticSortOrder);
      }

      // remove from pending submissions to prevent duplicate submissions
      if (this.pendingReactionSubmissions && this.pendingReactionSubmissions.has(optimisticSortOrder)) {
        this.pendingReactionSubmissions.delete(optimisticSortOrder);
      }

      // remove from ui
      await this.removeOptimisticReaction(message_id, optimisticSortOrder);
    } else {
      // real reaction - send DELETE request to server
      logDebug('live-journal', 'Removing real reaction', { channel_id, message_id, reaction_id });
      await apiDelete(`/channels/${channel_id}/messages/${message_id}/reactions/${reaction_id}`);
    }
  }

  async removeOptimisticReaction(messageId, optimisticSortOrder) {
    const optimisticId = `optimistic_${optimisticSortOrder}`;
    await db.messages
      .where('id')
      .equals(messageId)
      .modify((message) => {
        if (message.reactions && message.reactions[optimisticId]) {
          delete message.reactions[optimisticId];
        }
      });

    window.dispatchEvent(
      new CustomEvent(LIVE_REACTION_EVENT, {
        detail: {
          action: 'destroy',
          data: { id: optimisticId, message_id: messageId, optimistic_sort_order: optimisticSortOrder },
        },
      }),
    );
  }
}
